/**
 * La figure v2 — le contrat de DONNÉES d'une figure de défi, son contrôle de
 * forme, et son dessin CALCULÉ dans le plan. Le dessin dans l'espace vit dans
 * `vue3d.ts` et rend la même liste d'affichage.
 *
 * ── Pourquoi un second moteur à côté de `resoudre.ts` ──────────────────────
 *
 * Le v1 (`FigureSpec`) décrit une figure par sa CONSTRUCTION — « un triangle
 * de côtés 5, 7 et 9 » — et son solveur en déduit les points. Le parcours v2
 * (docs 21 et 22, décision AD-V2-04) la décrit par ses DONNÉES : des points
 * posés dans un repère et des objets qui s'y réfèrent. C'est ce que la
 * génération de la banque sec1 écrit déjà. Le v1 reste en place pour l'ancien
 * parcours ; rien ici ne le touche ni ne s'en sert.
 *
 * Le partage des rôles ne se rouvre pas : le modèle produit des DONNÉES, le
 * code vérifie leur FORME et CALCULE le dessin. Le composant React Native ne
 * fait aucune géométrie : il reçoit une liste d'affichage fermée, déjà en
 * coordonnées d'écran, et la recopie en SVG.
 *
 * ── Tolérer l'emballage, jamais le contenu ─────────────────────────────────
 *
 * Une même donnée écrite de deux façons est acceptée : un segment par les noms
 * de ses extrémités OU par leurs coordonnées, `xMin` ou `xmin`, `arête` ou
 * `arete`, un cône par son rayon OU par son diamètre, une quadratique en
 * forme générale OU canonique. Une donnée absente ou incohérente est un grief
 * NOMMÉ — point référencé absent, repère retourné, angle marqué droit qui ne
 * l'est pas, graduations écrites qui contredisent le pas — et la figure n'est
 * pas dessinée. Jamais un dessin inventé pour combler un trou.
 *
 * ── Constat du 2026-09-11 : ce que la banque contient vraiment ─────────────
 *
 * Les 12 figures publiées (sec1) sont toutes dans le plan : `point` ×110,
 * `etiquette` ×53, `segment` ×24 (16 par noms, 8 par coordonnées),
 * `polygone` ×19, `cote` ×12, `angle` ×9. `etiquette` n'est PAS dans le
 * doc 22, mais le validateur de la génération l'accepte et la banque en est
 * pleine : on l'accepte. Aucun cercle, aucune fonction, aucun solide encore —
 * la sec3 en aura, ils sont couverts.
 *
 * Deux usages de la banque ont dicté des règles de dessin :
 *
 *  1. Les diagrammes à bandes (MAT-1-STAT-BASE) ont un repère de 5 unités de
 *     large sur 25 à 42 de haut. En échelle égale, la figure serait cinq fois
 *     plus haute que large. D'où : l'échelle est ÉGALE en x et en y, SAUF dans
 *     un repère à axes sans objet euclidien (ni cercle, ni angle, ni cote) —
 *     là, ce sont les graduations qui disent les valeurs, et une bande peut
 *     s'étirer sans mentir. Un angle droit étiré, lui, mentirait.
 *  2. Ces mêmes diagrammes écrivent leurs graduations EUX-MÊMES, en
 *     étiquettes (« 8 » posé à y = 8), et l'énoncé s'y réfère (« une échelle
 *     de 8 boissons par intervalle »). Un pas « rond » calculé (5 ou 10)
 *     aurait tracé une grille qui contredit l'énoncé. D'où : quand la figure
 *     écrit ses propres graduations le long d'un axe, le code n'en écrit
 *     aucune et règle traits et grille sur les siennes ; quand elle y écrit
 *     des catégories (« Patin », « Ski »), l'axe n'est pas gradué du tout.
 *
 * ── Le cadre ───────────────────────────────────────────────────────────────
 *
 * Défaut relevé dans l'application actuelle : des figures coupées, parce que
 * le cadre suivait la géométrie seule et que le composant fixait un rapport
 * sur la largeur, débord caché. Ici le `viewBox` englobe TOUT ce qui est
 * dessiné — les textes compris, leur boîte estimée par la métrique de la
 * police — plus une marge, et le rapport largeur/hauteur est rendu avec. Le
 * composant n'a qu'à le respecter.
 */
import { BASES_SOLIDE } from '../types/figures.js';
import { barycentre, fois, formaterNombre, milieu, moins, norme, normale, plus, unitaire, } from './geometrie.js';
import { IndexCollisions, centreNomFige, chevauchent, segmentTraverse, } from './placement-etiquettes.js';
import { AIR_FLECHE_NOM, NOM_DE_VECTEUR_FIGURE, flecheDuNomV2, placerNomDeVecteurV2, } from './nom-de-vecteur.js';
/* ================================================================ métrique */
/**
 * Largeur de référence du dessin, en unités d'affichage — celle du v1
 * (`LARGEUR_RESOLUE`). Police et traits sont exprimés dans ce cadre ; le
 * composant met le `viewBox` à l'échelle de sa largeur réelle.
 */
export const LARGEUR_REFERENCE_V2 = 320;
/** Au-delà de ce rapport hauteur/largeur, la fenêtre est plafonnée en hauteur (valeur du v1). */
const RATIO_MAX = 1.3;
/** En deçà, une figure DÉFORMABLE est haussée ; une figure euclidienne reste une bande mince. */
const RATIO_MIN = 0.42;
/** Taille de police de TOUS les textes de la figure, en unités d'affichage (celle du v1). */
export const TAILLE_POLICE_V2 = 13;
/**
 * Métrique d'IBM Plex Mono, la police des figures (la même que le v1), en
 * fraction de la taille : chasse fixe, montante, descendante. C'est ce qui
 * permet d'estimer la boîte d'un texte sans le mesurer — et donc de garantir,
 * ici et non dans le composant, qu'il tient dans le cadre.
 */
const CHASSE = 0.65;
const MONTANTE = 0.78;
const DESCENDANTE = 0.3;
/** Rayon du disque qui marque un point. */
export const RAYON_MARQUE_V2 = 2.6;
/**
 * Rayon d'une BORNE (tranche T42) : le point plein ou vide qui ferme un morceau
 * de courbe ou un segment — plus grand qu'un point nommé, pour qu'un point vide
 * se lise comme un cercle et non comme une tache, et que plein et vide aient la
 * même taille côte à côte.
 */
export const RAYON_BORNE_V2 = 4;
/** L'épaisseur du contour d'un point VIDE : celle d'un trait de figure, un peu moins qu'une courbe. */
export const EPAISSEUR_CREUX_V2 = 1.6;
/** L'écart entre deux hachures d'un demi-plan, en unités d'affichage. */
const PAS_HACHURE_V2 = 9;
/** De combien une hachure s'arrête avant la frontière STRICTE d'une région ou d'un demi-plan : ses tirets restent lisibles. */
const RETRAIT_HACHURE_STRICTE_V2 = 5;
/** Marge autour de tout ce qui est dessiné. */
const MARGE_CADRE = 6;
/** Distance entre un point et la boîte de son nom. */
const ECART_NOM = 3;
/** Décalage de la ligne de cote par rapport au segment coté. */
const ECART_COTE = 12;
/** Prolongement d'un axe au-delà de la fenêtre, pour sa flèche. */
const PROLONGEMENT_AXE = 10;
/** Longueur et demi-largeur d'une pointe de flèche. */
const FLECHE_LONGUEUR = 7;
const FLECHE_DEMI = 3;
/* ============================================================ vocabulaire */
export const FAMILLES_FONCTION_V2 = [
    'lineaire',
    'quadratique',
    'exponentielle',
    'valeur_absolue',
    'racine',
    /* Tranche T22 (F-85, 11 octobre 2026) : la courbe du logarithme. */
    'logarithmique',
    /* Tranche T35 : le quotient de deux polynômes du premier degré. */
    'rationnelle',
    /* MAT-5-FTRIGO (17 octobre 2026) : a·sin b(x − h) + k et a·cos b(x − h) + k. */
    'sinusoidale',
    /* MAT-5-FTRIGO (18 octobre 2026, CAP-SIN) : a·tan b(x − h) + k, coupée à chaque asymptote. */
    'tangente',
];
/**
 * Les familles qu'un ÉLÈVE trace dans l'atelier (l'outil « fonction »), donc
 * celles qu'un attendu `courbe_egale` ou `parties_egales` peut exiger. Une
 * famille n'y entre qu'avec sa forme dans le panneau du repère : sans elle, un
 * exercice qui demanderait de la tracer serait impossible à réussir.
 */
export const FAMILLES_TRACEES_V2 = [
    'lineaire',
    'quadratique',
    'exponentielle',
    'valeur_absolue',
    'racine',
    'logarithmique',
    'rationnelle',
    /* Tranche T44 (CAP-SIN, « trace_eleve ») : « Sinusoïdale » et « Tangente » dans l'atelier. */
    'sinusoidale',
    'tangente',
];
export const FAMILLES_SOLIDE_V2 = [
    'cube',
    'prisme_droit',
    'pyramide_droite',
    'cylindre',
    'cone',
    'boule',
];
/** Ce qu'une cote de solide peut mesurer. La liste permise dépend de la famille (`GRANDEURS_PAR_FAMILLE`). */
export const GRANDEURS_COTE_V2 = [
    'hauteur',
    'rayon',
    'diametre',
    'apotheme',
    'arete',
    'cote_base',
    'profondeur_base',
    'diagonale_base',
];
/**
 * De quel côté de sa frontière un demi-plan est hachuré (atelier graphique,
 * phase 3). Le côté se dit en mots plutôt qu'en signe d'inéquation : c'est ce
 * que l'élève voit et ce que la consigne écrit. « dessus » et « dessous » ne
 * disent rien d'une frontière verticale, « gauche » et « droite » rien d'une
 * horizontale — la normalisation refuse ces deux couples.
 */
export const COTES_DEMI_PLAN_V2 = ['dessus', 'dessous', 'gauche', 'droite'];
/**
 * De quel côté de sa courbe une `region` est hachurée : au-dessus (`y ≥ f(x)`)
 * ou au-dessous. Les mêmes mots que le demi-plan ; « gauche » et « droite » ne
 * disent rien du graphique d'une fonction de x.
 */
export const COTES_REGION_V2 = ['dessus', 'dessous'];
/**
 * Le bout d'une partie d'une fonction définie par parties (tranche T42) : un
 * point PLEIN (borne comprise), VIDE (borne exclue), ou AUCUN (la partie se
 * prolonge au-delà, sans point marqué — jusqu'au bord de la fenêtre).
 */
export const BOUTS_V2 = ['plein', 'vide', 'aucun'];
/** Le nombre de parties d'une fonction définie par parties : deux au moins, huit au plus. */
export const PARTIES_MIN_V2 = 2;
export const PARTIES_MAX_V2 = 8;
/**
 * LES CONIQUES (tranche T38) — le cercle, l'ellipse, l'hyperbole, la parabole du
 * plan cartésien. Une conique n'est le graphique d'aucune FONCTION : elle a son
 * objet, écrit par les nombres de son équation.
 *
 * Sa RÉGION se dit par rapport à elle : `interieur` est la partie qui contient
 * le centre d'un cercle ou d'une ellipse, et le ou les FOYERS d'une hyperbole
 * ou d'une parabole ; `exterieur`, l'autre.
 */
export const COTES_CONIQUE_V2 = ['interieur', 'exterieur'];
/** `horizontale` : x²/a² − y²/b² = 1, sommets (±a ; 0). `verticale` : y²/b² − x²/a² = 1, sommets (0 ; ±b). */
export const OUVERTURES_HYPERBOLE_V2 = ['horizontale', 'verticale'];
/** `vertical` : (x − h)² = 4c(y − k). `horizontal` : (y − k)² = 4c(x − h) — la parabole « couchée ». */
export const AXES_PARABOLE_V2 = ['vertical', 'horizontal'];
/**
 * Le TON d'un tracé de l'atelier graphique (doc 26, § 1.5) : l'énoncé en
 * calque, le travail de l'élève, l'attendu montré après correction, l'écart.
 * Absent : les couleurs des calques, comme toute figure de la banque.
 */
/** Le remplissage d'une SURFACE (secteur) : la teinte des surfaces de la figure, ou la couleur du fond. */
export const TONS_SURFACE_V2 = ['teinte', 'fond'];
export const TONS_TRACE = ['enonce', 'eleve', 'attendu', 'ecart'];
/** La vue d'un solide quand la figure n'en donne pas. */
export const VUE_PAR_DEFAUT_V2 = { azimut: 30, elevation: 20 };
/**
 * L'élévation est bornée à ±89° : à 90° pile, l'azimut n'a plus de sens et le
 * geste de rotation « saute ». L'élève ne voit pas la différence d'un degré.
 */
export const ELEVATION_LIMITE_V2 = 89;
/* ============================================================ outils texte */
/** Largeur et hauteur estimées d'un texte, par la métrique de la police. */
export function dimensionsTexteV2(texte, taille = TAILLE_POLICE_V2) {
    return {
        largeur: [...texte].length * CHASSE * taille,
        hauteur: (MONTANTE + DESCENDANTE) * taille,
    };
}
/** La boîte estimée d'un texte de la liste d'affichage. Les tests et le composant (zone de toucher) s'en servent. */
export function boiteTexteV2(t) {
    const { largeur, hauteur } = dimensionsTexteV2(t.texte, t.taille);
    const x = t.ancre === 'debut' ? t.x : t.ancre === 'fin' ? t.x - largeur : t.x - largeur / 2;
    return { x, y: t.y - MONTANTE * t.taille, largeur, hauteur };
}
/** Un texte dont la BOÎTE est centrée sur `c`. */
export function texteCentreV2(c, texte, role, taille = TAILLE_POLICE_V2) {
    return {
        type: 'texte',
        x: c.x,
        y: c.y + ((MONTANTE - DESCENDANTE) / 2) * taille,
        texte,
        ancre: 'milieu',
        role,
        taille,
    };
}
/**
 * Le centre d'une boîte `largeur × hauteur` posée à `ecart` de `p`, dans la
 * direction unitaire `d`. Le décalage ajoute la demi-étendue de la boîte LE
 * LONG de `d` : la boîte entière reste du bon côté de la droite qui passe à
 * `ecart` de `p` — un nom ne retombe jamais sur son point, une cote jamais sur
 * son trait.
 */
export function centreDecaleV2(p, d, ecart, largeur, hauteur) {
    const e = (Math.abs(d.x) * largeur) / 2 + (Math.abs(d.y) * hauteur) / 2;
    return { x: p.x + d.x * (ecart + e), y: p.y + d.y * (ecart + e) };
}
/** Une pointe de flèche pleine, `pointe` au bout, `direction` unitaire vers la pointe. */
export function pointeFlecheV2(pointe, direction, calque) {
    const base = moins(pointe, fois(direction, FLECHE_LONGUEUR));
    const n = normale(direction);
    return {
        type: 'polygone',
        points: [pointe, plus(base, fois(n, FLECHE_DEMI)), moins(base, fois(n, FLECHE_DEMI))],
        rempli: true,
        calque,
    };
}
/** Le rectangle qui englobe une liste de primitives, `null` si elle est vide. */
export function englobantV2(primitives) {
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    const prendre = (x, y) => {
        x0 = Math.min(x0, x);
        y0 = Math.min(y0, y);
        x1 = Math.max(x1, x);
        y1 = Math.max(y1, y);
    };
    for (const p of primitives) {
        switch (p.type) {
            case 'trait':
                prendre(p.x1, p.y1);
                prendre(p.x2, p.y2);
                break;
            case 'polyligne':
            case 'polygone':
            case 'arc':
            case 'carre_droit':
                for (const q of p.points)
                    prendre(q.x, q.y);
                break;
            case 'cercle':
            case 'marque':
                prendre(p.cx - p.r, p.cy - p.r);
                prendre(p.cx + p.r, p.cy + p.r);
                break;
            case 'texte': {
                const b = boiteTexteV2(p);
                prendre(b.x, b.y);
                prendre(b.x + b.largeur, b.y + b.hauteur);
                break;
            }
        }
    }
    if (!Number.isFinite(x0))
        return null;
    return { x: x0, y: y0, largeur: x1 - x0, hauteur: y1 - y0 };
}
function estObjet(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
/**
 * Une clé comparée sans casse, sans accents, sans soulignés ni espaces :
 * `xMin` ≡ `xmin` ≡ `x_min`, `arête` ≡ `arete`, `valeur_absolue` ≡
 * `valeurAbsolue`. C'est de l'emballage.
 */
function cleNeutre(k) {
    return k
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\s_-]/g, '')
        .toLowerCase();
}
/** La valeur du premier champ de `o` dont la clé neutre est l'une de `noms`. */
function champ(o, ...noms) {
    const voulus = new Set(noms.map(cleNeutre));
    for (const [k, v] of Object.entries(o))
        if (voulus.has(cleNeutre(k)))
            return v;
    return undefined;
}
/**
 * Un nombre fini, écrit en nombre ou en chaîne (« 2,5 », « 2.5 », « −3 »).
 * Rend `null` pour tout le reste — une chaîne qui n'est pas un nombre n'en
 * devient pas un.
 */
function nombre(v) {
    if (typeof v === 'number')
        return Number.isFinite(v) ? v : null;
    if (typeof v !== 'string')
        return null;
    const s = v.trim().replace('−', '-');
    if (!/^[-+]?\d+(?:[.,]\d+)?$/.test(s))
        return null;
    return Number(s.replace(',', '.'));
}
const BORNE_COORDONNEE = 1000;
const OBJETS_MAX = 300;
const TEXTE_MAX = 40;
const NOM_MAX = 8;
/** Écart toléré entre l'angle mesuré et 90° pour un angle marqué droit, en degrés. */
const TOLERANCE_ANGLE_DROIT = 0.5;
/**
 * Un texte écrit DANS la figure. Il est dessiné tel quel en SVG : pas de
 * LaTeX (il s'afficherait brut), et la virgule décimale, comme partout ici.
 */
function texteAffiche(v, quoi, dit) {
    if (typeof v !== 'string' || v.trim() === '') {
        dit(`${quoi} : texte absent`);
        return null;
    }
    const t = v.trim();
    if ([...t].length > TEXTE_MAX)
        dit(`${quoi} : texte de plus de ${TEXTE_MAX} caractères`);
    if (/\\[([]|\$/.test(t))
        dit(`${quoi} : « ${t} » est du LaTeX — la figure n'écrit que du texte`);
    if (/\d\.\d/.test(t))
        dit(`${quoi} : point décimal dans « ${t} » — la virgule est la notation d'ici`);
    return t;
}
function coordonnee(v, quoi, dit) {
    const n = nombre(v);
    if (n === null) {
        dit(`${quoi} : ${JSON.stringify(v) ?? 'rien'} n'est pas un nombre`);
        return null;
    }
    if (Math.abs(n) > BORNE_COORDONNEE) {
        dit(`${quoi} : ${formaterNombre(n)} hors des bornes ±${BORNE_COORDONNEE}`);
        return null;
    }
    return n;
}
const memePoint = (p, q) => Math.abs(p.x - q.x) < 1e-9 && Math.abs(p.y - q.y) < 1e-9;
/** Un point désigné par son nom ou écrit `{ x, y }`. `undefined` : rien d'écrit. */
function refPoint(v, points, quoi, dit) {
    if (v === undefined || v === null)
        return undefined;
    if (typeof v === 'string') {
        const p = points.get(v.trim());
        if (p === undefined)
            dit(`${quoi} : point « ${v} » absent`);
        return p ?? null;
    }
    if (estObjet(v)) {
        const x = coordonnee(champ(v, 'x'), `${quoi} (x)`, dit);
        const y = coordonnee(champ(v, 'y'), `${quoi} (y)`, dit);
        return x === null || y === null ? null : { x, y };
    }
    dit(`${quoi} : ni un nom de point ni des coordonnées`);
    return null;
}
/**
 * Une extrémité de segment ou de cote, par NOM (`de`/`a`) ou par COORDONNÉES
 * (`x1`,`y1` / `x2`,`y2`). Les deux écritures sont dans la banque (16 et 8).
 * Si les deux sont données, elles doivent désigner le même point.
 */
function extremite(o, cle, points, quoi, dit) {
    const [cx, cy] = cle === 'de' ? ['x1', 'y1'] : ['x2', 'y2'];
    const parNom = refPoint(champ(o, cle), points, `${quoi}, extrémité « ${cle} »`, dit);
    const bx = champ(o, cx);
    const by = champ(o, cy);
    let parCoord;
    if (bx !== undefined || by !== undefined) {
        const x = coordonnee(bx, `${quoi} (${cx})`, dit);
        const y = coordonnee(by, `${quoi} (${cy})`, dit);
        parCoord = x === null || y === null ? null : { x, y };
    }
    if (parNom === undefined && parCoord === undefined) {
        dit(`${quoi} : extrémité « ${cle} » absente (ni nom, ni ${cx}/${cy})`);
        return null;
    }
    if (parNom && parCoord && !memePoint(parNom, parCoord)) {
        dit(`${quoi} : l'extrémité « ${cle} » et (${cx}, ${cy}) désignent deux points différents`);
        return null;
    }
    return parNom ?? parCoord ?? null;
}
/* ========================================================== normalisation */
/**
 * Contrôle la FORME d'une figure v2 et la rend dans une écriture unique, prête
 * à dessiner — ou rend la liste NOMMÉE de tout ce qui cloche. Jamais les deux :
 * une figure à moitié juste n'est pas dessinée à moitié.
 */
export function normaliserFigureV2(brut) {
    const griefs = [];
    const dit = (q) => griefs.push(q);
    if (!estObjet(brut))
        return { griefs: ["figure : ce n'est pas un objet"] };
    let taille = 'pleine';
    const t = champ(brut, 'taille');
    if (t !== undefined) {
        if (t === 'pleine' || t === 'partielle')
            taille = t;
        else
            dit(`figure : taille « ${String(t)} » ni « pleine » ni « partielle »`);
    }
    const espace = champ(brut, 'espace');
    let figure = null;
    if (espace === 'plan')
        figure = normaliserPlan(brut, taille, dit);
    else if (espace === 'espace')
        figure = normaliserEspace(brut, taille, dit);
    else
        dit(`figure : espace « ${String(espace)} » ni « plan » ni « espace »`);
    if (griefs.length > 0 || figure === null) {
        return { griefs: griefs.length > 0 ? griefs : ['figure : illisible'] };
    }
    return { figure };
}
/* ----------------------------------------------------------------- plan */
const TYPES_PLAN = [
    'point',
    'etiquette',
    'segment',
    'droite',
    'polygone',
    'cercle',
    'fonction',
    'cote',
    'angle',
    'vecteur',
    'demi_plan',
    /* Tranche T28 (F-87) : une portion de cercle au trait, une part de disque en surface. */
    'arc',
    'secteur',
    /* Tranche T32 (F-88) : la région d'un côté d'une courbe. */
    'region',
    /* Tranche T38 : les coniques qui ne sont le graphique d'aucune fonction. */
    'ellipse',
    'hyperbole',
    'parabole',
    /* Tranche T42 : la fonction définie par parties, ses bornes pleines et vides. */
    'par_parties',
];
function normaliserRepere(v, dit) {
    if (!estObjet(v)) {
        dit('repère : absent');
        return null;
    }
    const lire = (cle) => coordonnee(champ(v, cle), `repère : ${cle}`, dit);
    const xMin = lire('xMin');
    const xMax = lire('xMax');
    const yMin = lire('yMin');
    const yMax = lire('yMax');
    if (xMin === null || xMax === null || yMin === null || yMax === null)
        return null;
    let bon = true;
    if (!(xMin < xMax)) {
        dit(`repère : xMin (${formaterNombre(xMin)}) n'est pas inférieur à xMax (${formaterNombre(xMax)})`);
        bon = false;
    }
    if (!(yMin < yMax)) {
        dit(`repère : yMin (${formaterNombre(yMin)}) n'est pas inférieur à yMax (${formaterNombre(yMax)})`);
        bon = false;
    }
    const booleen = (cle) => {
        const b = champ(v, cle);
        if (b === undefined)
            return false;
        if (typeof b !== 'boolean') {
            dit(`repère : « ${cle} » doit valoir true ou false`);
            return false;
        }
        return b;
    };
    const axes = booleen('axes');
    const grille = booleen('grille');
    let pas = null;
    let enPi = null;
    const p = champ(v, 'pas');
    if (p !== undefined) {
        const bx = estObjet(p) ? champ(p, 'x') : p;
        const by = estObjet(p) ? champ(p, 'y') : p;
        const px = nombreOuPiV2(bx);
        const py = nombreOuPiV2(by);
        /* Un pas écrit en π (et non en nombre) : ses graduations s'écrivent en π. */
        const ecritEnPi = (b) => typeof b === 'string' && nombre(b) === null;
        if (ecritEnPi(bx) || ecritEnPi(by))
            enPi = { x: ecritEnPi(bx), y: ecritEnPi(by) };
        if (px === null || py === null || !(px > 0) || !(py > 0)) {
            dit('repère : pas non positif');
            bon = false;
        }
        else {
            pas = { x: px, y: py };
            if (bon && ((xMax - xMin) / px > 200 || (yMax - yMin) / py > 200)) {
                dit('repère : pas trop fin — plus de 200 graduations sur un axe');
                bon = false;
            }
        }
    }
    if (!bon)
        return null;
    return enPi === null
        ? { xMin, xMax, yMin, yMax, axes, grille, pas }
        : { xMin, xMax, yMin, yMax, axes, grille, pas, enPi };
}
/** Une graduation en π : « π/2 », « π », « 3π/2 », « −π/4 », « 0 » ; un nombre hors des kπ/n (n ≤ 12) : en décimal. */
export function texteEnPiV2(v) {
    if (Math.abs(v) < 1e-12)
        return '0';
    for (let n = 1; n <= 12; n += 1) {
        const k = Math.round((v * n) / Math.PI);
        if (k !== 0 && Math.abs((k * Math.PI) / n - v) < 1e-9) {
            const a = Math.abs(k);
            return `${k < 0 ? '−' : ''}${a === 1 ? '' : a}π${n === 1 ? '' : `/${n}`}`;
        }
    }
    return formaterNombre(v);
}
/**
 * Le repère NORMALISÉ récrit tel qu'un document l'écrit (`RepereV2Dto`) — sans
 * perdre l'axe gradué en π (tranche T44) : son pas se récrit « π/2 », que
 * `normaliserRepereV2` relit en nombre ET en graduation en π. Hors des kπ/n
 * (n ≤ 12), le nombre exact.
 */
export function repereV2VersDto(r) {
    const pas = (v, enPi) => {
        const t = enPi === true ? texteEnPiV2(v) : '';
        return t.includes('π') ? t : v;
    };
    const px = r.pas === null ? null : pas(r.pas.x, r.enPi?.x);
    const py = r.pas === null ? null : pas(r.pas.y, r.enPi?.y);
    return {
        xMin: r.xMin,
        xMax: r.xMax,
        yMin: r.yMin,
        yMax: r.yMax,
        axes: r.axes,
        grille: r.grille,
        ...(px === null || py === null ? {} : { pas: px === py ? px : { x: px, y: py } }),
    };
}
/**
 * Le repère seul, contrôlé et normalisé comme celui d'une figure (atelier
 * graphique : la fenêtre d'un document de construction).
 */
export function normaliserRepereV2(brut) {
    const griefs = [];
    const repere = normaliserRepere(brut, (q) => griefs.push(q));
    return repere === null || griefs.length > 0
        ? { griefs: griefs.length > 0 ? griefs : ['repère : illisible'] }
        : { repere };
}
function normaliserPlan(o, taille, dit) {
    const repere = normaliserRepere(champ(o, 'repere'), dit);
    const bruts = champ(o, 'objets');
    if (!Array.isArray(bruts) || bruts.length === 0) {
        dit('figure : aucun objet');
        return null;
    }
    if (bruts.length > OBJETS_MAX)
        dit(`figure : ${bruts.length} objets, ${OBJETS_MAX} au plus`);
    /* Premier passage : les points, pour qu'un objet puisse nommer un point
       déclaré APRÈS lui — l'ordre d'écriture est de l'emballage. */
    const points = new Map();
    const types = [];
    bruts.forEach((b, i) => {
        const quoi = `objet ${i + 1}`;
        if (!estObjet(b)) {
            dit(`${quoi} : ce n'est pas un objet`);
            types.push(null);
            return;
        }
        const tb = champ(b, 'type');
        const type = TYPES_PLAN.find((t) => typeof tb === 'string' && cleNeutre(t) === cleNeutre(tb));
        if (type === undefined) {
            dit(`${quoi} : type « ${String(tb)} » inconnu`);
            types.push(null);
            return;
        }
        types.push(type);
        if (type !== 'point')
            return;
        const nomBrut = champ(b, 'nom');
        const nom = typeof nomBrut === 'string' ? nomBrut.trim() : '';
        /* Un point SANS NOM est une borne (tranche T42) : il est lu avec les autres
           objets, et contrôlé après eux. Il ne se désigne pas : il n'entre pas ici. */
        if (nom === '' && (nomBrut === undefined || typeof nomBrut === 'string'))
            return;
        if (nom === '' || [...nom].length > NOM_MAX) {
            dit(`${quoi} (point) : nom absent ou de plus de ${NOM_MAX} caractères`);
            return;
        }
        const x = coordonnee(champ(b, 'x'), `${quoi} (point ${nom}) : x`, dit);
        const y = coordonnee(champ(b, 'y'), `${quoi} (point ${nom}) : y`, dit);
        if (x === null || y === null)
            return;
        if (points.has(nom))
            dit(`${quoi} : point « ${nom} » déclaré deux fois`);
        points.set(nom, { x, y });
    });
    const objets = [];
    /** Les points sans nom ni `creux` : ils doivent être le bout d'un trait (tranche T42). */
    const boutsAControler = [];
    bruts.forEach((b, i) => {
        const type = types[i];
        if (!estObjet(b) || type === null || type === undefined)
            return;
        const quoi = `objet ${i + 1} (${type})`;
        const objet = normaliserObjet(type, b, points, repere, quoi, dit);
        if (objet === null)
            return;
        objets.push(marquer(objet, b, quoi, dit));
        if (objet.type === 'point' && objet.nom === '' && champ(b, 'creux') === undefined) {
            boutsAControler.push({ p: objet, quoi });
        }
    });
    if (boutsAControler.length > 0) {
        const bouts = boutsDesTraits(objets);
        for (const { p, quoi } of boutsAControler) {
            if (!bouts.some((q) => presDe(p.x, q.x) && presDe(p.y, q.y))) {
                dit(`${quoi} : point sans nom en (${formaterNombre(p.x)} ; ${formaterNombre(p.y)}) — ` +
                    'un point sans nom est une borne : écrire « creux » (true vide, false plein), ' +
                    "ou le poser au bout d'un segment ou d'une courbe bornée");
            }
        }
    }
    if (repere === null)
        return null;
    const figure = { espace: 'plan', taille, repere, objets };
    /* Les graduations écrites par la figure doivent s'accorder avec le pas. */
    const echelle = echellePlan(repere, objets);
    for (const g of analyserAxes(repere, etiquettesDe(objets), echelle.kx === echelle.ky).griefs) {
        dit(g);
    }
    return figure;
}
/** Longueur maximale d'un identifiant d'objet (atelier graphique). */
const ID_MAX = 40;
/**
 * Recopie l'`id` et le `ton` d'un objet, quand il en porte (atelier
 * graphique). Aucune figure de la banque n'en porte : elle est rendue telle
 * quelle, sans une clé de plus.
 */
function marquer(objet, b, quoi, dit) {
    const id = champ(b, 'id');
    const ton = champ(b, 'ton');
    if (id === undefined && ton === undefined)
        return objet;
    let marque = {};
    if (id !== undefined) {
        if (typeof id !== 'string' || id.trim() === '' || [...id].length > ID_MAX) {
            dit(`${quoi} : identifiant absent ou de plus de ${ID_MAX} caractères`);
        }
        else
            marque = { ...marque, id };
    }
    if (ton !== undefined) {
        const t = TONS_TRACE.find((x) => x === ton);
        if (t === undefined)
            dit(`${quoi} : ton « ${String(ton)} » inconnu (${TONS_TRACE.join(', ')})`);
        else
            marque = { ...marque, ton: t };
    }
    return { ...objet, ...marque };
}
function normaliserObjet(type, b, points, repere, quoi, dit) {
    switch (type) {
        case 'point': {
            const cb = champ(b, 'creux');
            if (cb !== undefined && typeof cb !== 'boolean') {
                dit(`${quoi} : « creux » vaut true (point vide) ou false (point plein)`);
                return null;
            }
            const creux = typeof cb === 'boolean' ? { creux: cb } : {};
            const nom = String(champ(b, 'nom') ?? '').trim();
            if (nom === '') {
                /* Une BORNE sans nom (tranche T42) : ses coordonnées sont lues ici. */
                const x = coordonnee(champ(b, 'x'), `${quoi} : x`, dit);
                const y = coordonnee(champ(b, 'y'), `${quoi} : y`, dit);
                if (x === null || y === null)
                    return null;
                return { type: 'point', nom, x, y, creux: cb === true };
            }
            const p = points.get(nom);
            return p === undefined ? null : { type: 'point', nom, x: p.x, y: p.y, ...creux };
        }
        case 'etiquette': {
            const texte = texteAffiche(champ(b, 'texte'), quoi, dit);
            const x = coordonnee(champ(b, 'x'), `${quoi} : x`, dit);
            const y = coordonnee(champ(b, 'y'), `${quoi} : y`, dit);
            return texte === null || x === null || y === null ? null : { type, texte, x, y };
        }
        case 'segment':
        case 'droite': {
            const de = extremite(b, 'de', points, quoi, dit);
            const a = extremite(b, 'a', points, quoi, dit);
            const s = champ(b, 'style');
            let style = 'plein';
            if (s !== undefined) {
                const sn = typeof s === 'string' ? cleNeutre(s) : '';
                if (sn === 'plein')
                    style = 'plein';
                else if (sn === 'pointille' || sn === 'tirets')
                    style = 'pointille';
                else
                    dit(`${quoi} : style « ${String(s)} » ni « plein » ni « pointille »`);
            }
            if (de === null || a === null)
                return null;
            if (memePoint(de, a)) {
                dit(`${quoi} : ses deux extrémités sont confondues`);
                return null;
            }
            return { type, de, a, style };
        }
        case 'polygone': {
            const brutsSommets = champ(b, 'sommets');
            if (!Array.isArray(brutsSommets) || brutsSommets.length < 3) {
                dit(`${quoi} : il faut au moins trois sommets`);
                return null;
            }
            const sommets = [];
            for (const [j, s] of brutsSommets.entries()) {
                const p = refPoint(s, points, `${quoi}, sommet ${j + 1}`, dit);
                if (p === undefined)
                    dit(`${quoi} : sommet ${j + 1} vide`);
                if (!p)
                    return null;
                sommets.push(p);
            }
            if (Math.abs(aireSignee(sommets)) < 1e-9) {
                dit(`${quoi} : polygone aplati, d'aire nulle`);
                return null;
            }
            /* `plein` (T28) : un polygone est teinté par défaut ; `false` ne trace que son contour. */
            const plein = champ(b, 'plein');
            if (plein !== undefined && plein !== true && plein !== false) {
                dit(`${quoi} : « plein » vaut vrai ou faux`);
                return null;
            }
            /* `contour` (T28) : `false` ne peint que la surface — pour une figure
               décomposée dont un côté, commun à deux pièces, se trace en pointillé. */
            const contour = champ(b, 'contour');
            if (contour !== undefined && contour !== true && contour !== false) {
                dit(`${quoi} : « contour » vaut vrai ou faux`);
                return null;
            }
            if (plein === false && contour === false) {
                dit(`${quoi} : ni surface ni contour — il n'y a rien à dessiner`);
                return null;
            }
            return {
                type,
                sommets,
                ...(plein === false ? { plein: false } : {}),
                ...(contour === false ? { contour: false } : {}),
            };
        }
        case 'arc':
        case 'secteur': {
            const centre = refPoint(champ(b, 'centre'), points, `${quoi}, centre`, dit);
            if (centre === undefined)
                dit(`${quoi} : centre absent`);
            const rayon = nombre(champ(b, 'rayon'));
            if (rayon === null || !(rayon > 0) || rayon > BORNE_COORDONNEE) {
                dit(`${quoi} : rayon absent ou non positif`);
                return null;
            }
            /* Les angles : en degrés, sens antihoraire, 0° vers la droite ; de `de` à `a`. */
            const de = nombre(champ(b, 'de'));
            const a = nombre(champ(b, 'a'));
            if (de === null || a === null) {
                dit(`${quoi} : « de » et « a » sont deux angles en degrés`);
                return null;
            }
            if (de < -360 || de > 360)
                dit(`${quoi} : « de » vaut ${formaterNombre(de)}° — entre −360 et 360`);
            if (!(a > de))
                dit(`${quoi} : il faut a > de (on tourne dans le sens antihoraire, de « de » à « a »)`);
            else if (a - de > 360 + 1e-9)
                dit(`${quoi} : de ${formaterNombre(de)}° à ${formaterNombre(a)}° — plus d'un tour`);
            if (!centre)
                return null;
            if (repere !== null &&
                (centre.x + rayon < repere.xMin ||
                    centre.x - rayon > repere.xMax ||
                    centre.y + rayon < repere.yMin ||
                    centre.y - rayon > repere.yMax)) {
                dit(`${quoi} : rien ne passe dans la fenêtre du repère`);
                return null;
            }
            if (type === 'arc') {
                const sb = champ(b, 'style');
                let style = 'plein';
                if (sb !== undefined) {
                    const sn = typeof sb === 'string' ? cleNeutre(sb) : '';
                    if (sn === 'plein')
                        style = 'plein';
                    else if (sn === 'pointille' || sn === 'tirets')
                        style = 'pointille';
                    else if (sn === 'fort')
                        style = 'fort';
                    else
                        dit(`${quoi} : style « ${String(sb)} » ni « plein », ni « pointille », ni « fort »`);
                }
                const tb = champ(b, 'texte');
                const texte = tb === undefined || tb === null ? null : texteAffiche(tb, quoi, dit);
                return { type, centre, rayon, de, a, style, texte };
            }
            const contour = champ(b, 'contour');
            if (contour !== undefined && contour !== true && contour !== false) {
                dit(`${quoi} : « contour » vaut vrai ou faux`);
                return null;
            }
            const tonBrut = champ(b, 'remplissage');
            const ton = TONS_SURFACE_V2.find((t) => t === tonBrut);
            if (tonBrut !== undefined && ton === undefined) {
                dit(`${quoi} : remplissage « ${String(tonBrut)} » ni « teinte » ni « fond »`);
                return null;
            }
            return {
                type,
                centre,
                rayon,
                de,
                a,
                contour: contour !== false,
                remplissage: ton ?? 'teinte',
            };
        }
        case 'cercle': {
            const brutCentre = champ(b, 'centre');
            const cx = champ(b, 'cx');
            const centre = brutCentre === undefined && cx !== undefined
                ? refPoint({ x: cx, y: champ(b, 'cy') }, points, `${quoi}, centre`, dit)
                : refPoint(brutCentre, points, `${quoi}, centre`, dit);
            if (centre === undefined)
                dit(`${quoi} : centre absent`);
            const rayon = nombre(champ(b, 'rayon'));
            if (rayon === null || !(rayon > 0) || rayon > BORNE_COORDONNEE) {
                dit(`${quoi} : rayon absent ou non positif`);
                return null;
            }
            if (!centre)
                return null;
            const region = lireRegionConique(b, quoi, dit);
            if (region === null)
                return null;
            const cercle = { type, centre, rayon, ...region };
            return regionConiqueVisible(cercle, repere, quoi, dit) ? cercle : null;
        }
        case 'ellipse':
        case 'hyperbole':
        case 'parabole':
            return normaliserConique(type, b, points, repere, quoi, dit);
        case 'fonction':
            return normaliserFonction(b, repere, quoi, dit);
        case 'region':
            return normaliserRegion(b, repere, quoi, dit);
        case 'par_parties':
            return normaliserParParties(b, repere, quoi, dit);
        case 'cote': {
            const de = extremite(b, 'de', points, quoi, dit);
            const a = extremite(b, 'a', points, quoi, dit);
            const texte = texteAffiche(champ(b, 'texte'), quoi, dit);
            if (de === null || a === null || texte === null)
                return null;
            if (memePoint(de, a)) {
                dit(`${quoi} : ses deux extrémités sont confondues`);
                return null;
            }
            return { type, de, a, texte };
        }
        case 'angle': {
            const sommet = refPoint(champ(b, 'sommet'), points, `${quoi}, sommet`, dit);
            const de = refPoint(champ(b, 'de'), points, `${quoi}, côté « de »`, dit);
            const a = refPoint(champ(b, 'a'), points, `${quoi}, côté « a »`, dit);
            for (const [cle, v] of [
                ['sommet', sommet],
                ['de', de],
                ['a', a],
            ]) {
                if (v === undefined)
                    dit(`${quoi} : « ${cle} » absent`);
            }
            const droitBrut = champ(b, 'droit');
            if (droitBrut !== undefined && typeof droitBrut !== 'boolean') {
                dit(`${quoi} : « droit » doit valoir true ou false`);
            }
            const droit = droitBrut === true;
            const tb = champ(b, 'texte');
            const texte = tb === undefined ? null : texteAffiche(tb, quoi, dit);
            if (!sommet || !de || !a)
                return null;
            if (memePoint(sommet, de) || memePoint(sommet, a)) {
                dit(`${quoi} : un côté part du sommet sans longueur`);
                return null;
            }
            if (droit) {
                const mesure = mesureAngle(sommet, de, a);
                if (Math.abs(mesure - 90) > TOLERANCE_ANGLE_DROIT) {
                    dit(`${quoi} : marqué droit, mais il mesure ${formaterNombre(mesure)}°`);
                    return null;
                }
            }
            return { type, sommet, de, a, droit, texte };
        }
        /* ───────────────────── phase 3 : vecteur, demi-plan ──────────────────── */
        case 'vecteur': {
            const de = extremite(b, 'de', points, quoi, dit);
            const a = extremite(b, 'a', points, quoi, dit);
            if (de === null || a === null)
                return null;
            if (memePoint(de, a)) {
                dit(`${quoi} : ses deux extrémités sont confondues`);
                return null;
            }
            /* Le nom fléché (tranche T43) : une ou deux lettres, sans LaTeX. */
            const nb = champ(b, 'nom');
            if (nb === undefined)
                return { type, de, a };
            if (typeof nb !== 'string' || !NOM_DE_VECTEUR_FIGURE.test(nb.trim())) {
                dit(`${quoi} : « nom » — une ou deux lettres, sans LaTeX (« u », « AB »)`);
                return null;
            }
            return { type, de, a, nom: nb.trim() };
        }
        case 'demi_plan': {
            const de = extremite(b, 'de', points, quoi, dit);
            const a = extremite(b, 'a', points, quoi, dit);
            const cb = champ(b, 'cote');
            const cote = COTES_DEMI_PLAN_V2.find((c) => typeof cb === 'string' && cleNeutre(c) === cleNeutre(cb));
            if (cote === undefined) {
                dit(`${quoi} : côté « ${String(cb)} » inconnu (${COTES_DEMI_PLAN_V2.join(', ')})`);
            }
            const sb = champ(b, 'stricte');
            if (sb !== undefined && typeof sb !== 'boolean') {
                dit(`${quoi} : « stricte » doit valoir true ou false`);
            }
            if (de === null || a === null || cote === undefined)
                return null;
            if (memePoint(de, a)) {
                dit(`${quoi} : sa frontière est dégénérée`);
                return null;
            }
            /* Un côté qui ne dit rien de la frontière ne hachure rien : c'est un
               défaut de la figure, pas un dessin vide qu'on laisserait passer. */
            const vertical = Math.abs(a.x - de.x) < 1e-9;
            const horizontal = Math.abs(a.y - de.y) < 1e-9;
            if ((vertical && (cote === 'dessus' || cote === 'dessous')) ||
                (horizontal && (cote === 'gauche' || cote === 'droite'))) {
                dit(`${quoi} : le côté « ${cote} » ne dit rien d'une telle frontière`);
                return null;
            }
            return { type, de, a, cote, stricte: sb === true };
        }
    }
}
/* ------------------------------------------------------------- coniques */
/**
 * Écart toléré entre un foyer ou un sommet DÉCLARÉ et sa place calculée : celui
 * d'un arrondi au centième (« 2,65 » pour √7). Au-delà, c'est une autre conique.
 */
const TOLERANCE_POINT_CONIQUE = 0.011;
/**
 * Ce que vaut l'équation d'une conique en (x, y) : POSITIF à l'intérieur, nul
 * sur elle, négatif à l'extérieur. L'intérieur d'une hyperbole ou d'une parabole
 * est la partie qui contient le ou les foyers.
 */
export function valeurConiqueV2(o, x, y) {
    switch (o.type) {
        case 'cercle': {
            const X = x - o.centre.x;
            const Y = y - o.centre.y;
            return 1 - (X * X + Y * Y) / (o.rayon * o.rayon);
        }
        case 'ellipse': {
            const X = (x - o.centre.x) / o.a;
            const Y = (y - o.centre.y) / o.b;
            return 1 - X * X - Y * Y;
        }
        case 'hyperbole': {
            const X = (x - o.centre.x) / o.a;
            const Y = (y - o.centre.y) / o.b;
            return o.ouverture === 'horizontale' ? X * X - Y * Y - 1 : Y * Y - X * X - 1;
        }
        case 'parabole': {
            const X = x - o.sommet.x;
            const Y = y - o.sommet.y;
            return o.axe === 'horizontal' ? 4 * o.c * X - Y * Y : 4 * o.c * Y - X * X;
        }
    }
}
/**
 * Les FOYERS d'une conique, CALCULÉS : c² = |a² − b²| sur le grand axe d'une
 * ellipse, c² = a² + b² sur l'axe des sommets d'une hyperbole, le sommet décalé
 * de `c` le long de l'axe d'une parabole. Un cercle n'a que son centre.
 */
export function foyersConiqueV2(o) {
    switch (o.type) {
        case 'cercle':
            return [o.centre];
        case 'ellipse': {
            if (o.a === o.b)
                return [o.centre];
            const c = Math.sqrt(Math.abs(o.a * o.a - o.b * o.b));
            return o.a > o.b
                ? [
                    { x: o.centre.x - c, y: o.centre.y },
                    { x: o.centre.x + c, y: o.centre.y },
                ]
                : [
                    { x: o.centre.x, y: o.centre.y - c },
                    { x: o.centre.x, y: o.centre.y + c },
                ];
        }
        case 'hyperbole': {
            const c = Math.hypot(o.a, o.b);
            return o.ouverture === 'horizontale'
                ? [
                    { x: o.centre.x - c, y: o.centre.y },
                    { x: o.centre.x + c, y: o.centre.y },
                ]
                : [
                    { x: o.centre.x, y: o.centre.y - c },
                    { x: o.centre.x, y: o.centre.y + c },
                ];
        }
        case 'parabole':
            return [
                o.axe === 'horizontal'
                    ? { x: o.sommet.x + o.c, y: o.sommet.y }
                    : { x: o.sommet.x, y: o.sommet.y + o.c },
            ];
    }
}
/** Les SOMMETS d'une conique : quatre pour une ellipse, deux pour une hyperbole, un pour une parabole ; aucun pour un cercle. */
export function sommetsConiqueV2(o) {
    switch (o.type) {
        case 'cercle':
            return [];
        case 'ellipse':
            return [
                { x: o.centre.x - o.a, y: o.centre.y },
                { x: o.centre.x + o.a, y: o.centre.y },
                { x: o.centre.x, y: o.centre.y - o.b },
                { x: o.centre.x, y: o.centre.y + o.b },
            ];
        case 'hyperbole':
            return o.ouverture === 'horizontale'
                ? [
                    { x: o.centre.x - o.a, y: o.centre.y },
                    { x: o.centre.x + o.a, y: o.centre.y },
                ]
                : [
                    { x: o.centre.x, y: o.centre.y - o.b },
                    { x: o.centre.x, y: o.centre.y + o.b },
                ];
        case 'parabole':
            return [o.sommet];
    }
}
const couple = (p) => `(${formaterNombre(p.x)} ; ${formaterNombre(p.y)})`;
/** `region` et `stricte` d'une conique ; `null` : mal écrits (le grief est dit). */
function lireRegionConique(b, quoi, dit) {
    const rb = champ(b, 'region');
    const sb = champ(b, 'stricte');
    if (sb !== undefined && typeof sb !== 'boolean') {
        dit(`${quoi} : « stricte » doit valoir true ou false`);
        return null;
    }
    if (rb === undefined) {
        if (sb === true) {
            dit(`${quoi} : « stricte » sans « region » — pour un trait en tirets, écrire "style": "pointille"`);
            return null;
        }
        return {};
    }
    const region = COTES_CONIQUE_V2.find((c) => typeof rb === 'string' && cleNeutre(c) === cleNeutre(rb));
    if (region === undefined) {
        dit(`${quoi} : région « ${String(rb)} » inconnue (${COTES_CONIQUE_V2.join(', ')})`);
        return null;
    }
    return { region, ...(sb === true ? { stricte: true } : {}) };
}
/** La région d'une conique a-t-elle un point au moins dans la fenêtre ? Sondée sur une grille serrée. */
function regionConiqueVisible(o, repere, quoi, dit) {
    if (o.region === undefined || repere === null)
        return true;
    const N = 60;
    const signe = o.region === 'interieur' ? 1 : -1;
    for (let i = 0; i <= N; i += 1) {
        for (let j = 0; j <= N; j += 1) {
            const x = repere.xMin + ((repere.xMax - repere.xMin) * i) / N;
            const y = repere.yMin + ((repere.yMax - repere.yMin) * j) / N;
            if (signe * valeurConiqueV2(o, x, y) > 0)
                return true;
        }
    }
    dit(`${quoi} : rien de la région ne passe dans la fenêtre du repère`);
    return false;
}
/**
 * Des points que la figure DÉCLARE être les foyers (ou les sommets) de sa conique :
 * chacun doit en être un, à l'arrondi près. L'auteur les calcule ; ceci le relit.
 */
function controlerPointsDeConique(brut, attendus, nom, regle, points, quoi, dit) {
    if (brut === undefined)
        return true;
    const refs = Array.isArray(brut) ? brut : [brut];
    let bon = true;
    refs.forEach((ref, i) => {
        const p = refPoint(ref, points, `${quoi}, ${nom} ${i + 1}`, dit);
        if (p === undefined)
            dit(`${quoi} : ${nom} ${i + 1} vide`);
        if (!p) {
            bon = false;
            return;
        }
        const tenu = attendus.some((q) => Math.abs(q.x - p.x) <= TOLERANCE_POINT_CONIQUE &&
            Math.abs(q.y - p.y) <= TOLERANCE_POINT_CONIQUE);
        if (!tenu) {
            const lesquels = attendus.map(couple).join(' et ');
            dit(`${quoi} : le ${nom} déclaré ${typeof ref === 'string' ? `« ${ref} » ` : ''}${couple(p)} ` +
                `n'en est pas un — ${regle} : ${lesquels}`);
            bon = false;
        }
    });
    return bon;
}
/** L'ellipse, l'hyperbole, la parabole : leurs nombres, leurs options, et ce qu'elles contrôlent. */
function normaliserConique(type, b, points, repere, quoi, dit) {
    const booleen = (cle) => {
        const v = champ(b, cle);
        if (v === undefined || v === false)
            return false;
        if (v === true)
            return true;
        dit(`${quoi} : « ${cle} » vaut vrai ou faux`);
        return null;
    };
    const positif = (cle, role) => {
        const v = nombre(champ(b, cle));
        if (v === null || !(v > 0) || v > BORNE_COORDONNEE) {
            dit(`${quoi} : « ${cle} » (${role}) absent ou non positif`);
            return null;
        }
        return v;
    };
    const sb = champ(b, 'style');
    const sn = typeof sb === 'string' ? cleNeutre(sb) : '';
    if (sb !== undefined && sn !== 'plein' && sn !== 'pointille' && sn !== 'tirets') {
        dit(`${quoi} : style « ${String(sb)} » ni « plein » ni « pointille »`);
        return null;
    }
    const pointille = sn === 'pointille' || sn === 'tirets' ? { pointille: true } : {};
    const region = lireRegionConique(b, quoi, dit);
    let conique;
    if (type === 'parabole') {
        const sommet = refPoint(champ(b, 'sommet'), points, `${quoi}, sommet`, dit);
        if (sommet === undefined)
            dit(`${quoi} : sommet absent`);
        const c = nombre(champ(b, 'c'));
        const cBon = c !== null && c !== 0 && Math.abs(c) <= BORNE_COORDONNEE;
        if (!cBon)
            dit(`${quoi} : « c » (la distance signée du sommet au foyer) absent ou nul`);
        const ab = champ(b, 'axe');
        const axe = AXES_PARABOLE_V2.find((x) => typeof ab === 'string' && cleNeutre(x) === cleNeutre(ab));
        if (axe === undefined) {
            dit(`${quoi} : axe « ${String(ab)} » inconnu (${AXES_PARABOLE_V2.join(', ')})`);
        }
        const directrice = booleen('directrice');
        if (!sommet || !cBon || axe === undefined || directrice === null || region === null) {
            return null;
        }
        const nue = { type, sommet, c, axe };
        /* `foyer: true` le marque d'un point ; un point écrit le CONTRÔLE. */
        const fb = champ(b, 'foyer');
        if (fb !== undefined &&
            typeof fb !== 'boolean' &&
            !controlerPointsDeConique(fb, foyersConiqueV2(nue), 'foyer', 'le foyer est à la distance c du sommet, sur l’axe', points, quoi, dit)) {
            return null;
        }
        conique = {
            ...nue,
            ...(fb === true ? { foyer: true } : {}),
            ...(directrice ? { directrice: true } : {}),
            ...pointille,
            ...region,
        };
    }
    else {
        const centre = refPoint(champ(b, 'centre'), points, `${quoi}, centre`, dit);
        if (centre === undefined)
            dit(`${quoi} : centre absent`);
        const a = positif('a', type === 'ellipse' ? 'le demi-axe horizontal' : 'le nombre sous x²');
        const bb = positif('b', type === 'ellipse' ? 'le demi-axe vertical' : 'le nombre sous y²');
        if (!centre || a === null || bb === null || region === null)
            return null;
        let nue;
        if (type === 'ellipse')
            nue = { type, centre, a, b: bb };
        else {
            const ob = champ(b, 'ouverture');
            const ouverture = OUVERTURES_HYPERBOLE_V2.find((x) => typeof ob === 'string' && cleNeutre(x) === cleNeutre(ob));
            if (ouverture === undefined) {
                dit(`${quoi} : ouverture « ${String(ob)} » inconnue (${OUVERTURES_HYPERBOLE_V2.join(', ')})`);
                return null;
            }
            const asymptotes = booleen('asymptotes');
            const rectangle = booleen('rectangle');
            if (asymptotes === null || rectangle === null)
                return null;
            nue = {
                type,
                centre,
                a,
                b: bb,
                ouverture,
                ...(asymptotes ? { asymptotes: true } : {}),
                ...(rectangle ? { rectangle: true } : {}),
            };
        }
        const foyersBons = controlerPointsDeConique(champ(b, 'foyers'), foyersConiqueV2(nue), 'foyer', type === 'ellipse' ? 'c² = a² − b², sur le grand axe' : 'c² = a² + b², sur l’axe des sommets', points, quoi, dit);
        const sommetsBons = controlerPointsDeConique(champ(b, 'sommets'), sommetsConiqueV2(nue), 'sommet', type === 'ellipse'
            ? 'les sommets sont à a et à b du centre'
            : 'les sommets sont à a (ou à b) du centre, sur l’axe d’ouverture', points, quoi, dit);
        if (!foyersBons || !sommetsBons)
            return null;
        conique = { ...nue, ...pointille, ...region };
    }
    if (repere !== null && tronconsConique(conique, repere).length === 0) {
        dit(`${quoi} : rien de la courbe ne passe dans la fenêtre du repère`);
        return null;
    }
    return regionConiqueVisible(conique, repere, quoi, dit) ? conique : null;
}
const FAMILLE_PAR_CLE = {
    lineaire: 'lineaire',
    affine: 'lineaire',
    quadratique: 'quadratique',
    exponentielle: 'exponentielle',
    valeurabsolue: 'valeur_absolue',
    racine: 'racine',
    racinecarree: 'racine',
    logarithmique: 'logarithmique',
    logarithme: 'logarithmique',
    rationnelle: 'rationnelle',
    quotient: 'rationnelle',
    inverse: 'rationnelle',
    sinusoidale: 'sinusoidale',
    sinusoide: 'sinusoidale',
    tangente: 'tangente',
};
const PARAMETRES_PERMIS = {
    lineaire: ['a', 'b'],
    quadratique: ['a', 'b', 'c', 'h', 'k'],
    exponentielle: ['a', 'b', 'c'],
    valeur_absolue: ['a', 'b', 'h', 'k'],
    racine: ['a', 'b', 'h', 'k'],
    logarithmique: ['a', 'b', 'c', 'h', 'k'],
    /* La forme canonique (a, b, h, k) OU le quotient (n1·x + n0) / (d1·x + d0) — jamais mêlés. */
    rationnelle: ['a', 'b', 'h', 'k', 'n1', 'n0', 'd1', 'd0'],
    /* `fonction` : "sin" (défaut) ou "cos" — le seul paramètre qui n'est pas un nombre. */
    sinusoidale: ['a', 'b', 'h', 'k', 'fonction'],
    tangente: ['a', 'b', 'h', 'k'],
};
/**
 * Un nombre écrit EXACTEMENT en π — "pi/3", "-pi/4", "2π/3", "\\tfrac{\\pi}{6}", "π" —,
 * ou un nombre ordinaire. Sert au b et au h d'une sinusoïde : un auteur écrit
 * h = π/3, pas 1,0471975511965976. `null` : illisible.
 */
export function nombreOuPiV2(v) {
    const n = nombre(v);
    if (n !== null || typeof v !== 'string')
        return n;
    let s = v
        .replace(/\$/g, '')
        .replace(/\\left|\\right/g, '')
        .replace(/\\[,;:! ]/g, '')
        .replace(/[−–]/g, '-')
        .replace(/\\pi|π/g, 'pi')
        .replace(/[\s*]/g, '');
    s = s.replace(/\\[dt]?frac\{([^{}]*)\}\{([^{}]*)\}/g, '$1/$2');
    const m = /^([+-]?)(\d*)pi(?:\/(\d+))?$/.exec(s) ?? /^([+-]?)(\d+)\/(\d+)pi$/.exec(s);
    if (m === null)
        return null;
    const k = (m[1] === '-' ? -1 : 1) * (m[2] === '' ? 1 : Number(m[2]));
    const d = m[3] === undefined ? 1 : Number(m[3]);
    return d === 0 ? null : (k * Math.PI) / d;
}
/** La famille d'une fonction, quelle qu'en soit l'écriture ; `null` : inconnue. */
export function familleFonctionV2(brut) {
    return typeof brut === 'string' ? (FAMILLE_PAR_CLE[cleNeutre(brut)] ?? null) : null;
}
/**
 * La forme CANONIQUE d'une fonction, lue de paramètres bruts — ou ce qui
 * cloche, sans préfixe (l'appelant pose le sien).
 *
 * Extraite de `normaliserFonction` pour que l'atelier graphique s'en serve :
 * la définition `fonction` d'une construction (phase 3) doit donner la MÊME
 * courbe que la figure d'un défi qui écrit les mêmes paramètres. Deux
 * conversions de la forme a(x − h)² + k, ce serait deux vérités, et un élève
 * pourrait être noté faux contre sa propre consigne.
 */
export function fonctionCanoniqueV2(famille, parametres, domaineBrut) {
    const griefs = [];
    const d = (q) => griefs.push(q);
    if (!estObjet(parametres))
        return { griefs: ['paramètres absents'] };
    /* La famille est TYPÉE, mais elle vient d'un document reçu : un « sinus »
       écrit à la main arriverait ici et ferait exploser la table des paramètres.
       On le dit plutôt que de laisser passer une exception. */
    const permis = PARAMETRES_PERMIS[famille];
    if (permis === undefined)
        return { griefs: [`famille « ${String(famille)} » inconnue`] };
    const lus = new Map();
    let trigo = 'sin';
    for (const [k, v] of Object.entries(parametres)) {
        const cle = cleNeutre(k);
        if (!permis.includes(cle)) {
            d(`paramètre « ${k} » inconnu pour cette famille`);
            continue;
        }
        /* La sinusoïde : sa fonction de base (un mot), et b, h exacts en π. */
        if (cle === 'fonction') {
            const f = typeof v === 'string' ? cleNeutre(v) : '';
            if (f === 'sin' || f === 'sinus')
                trigo = 'sin';
            else if (f === 'cos' || f === 'cosinus')
                trigo = 'cos';
            else
                d(`« fonction » vaut ${String(v)} : "sin" ou "cos"`);
            continue;
        }
        const n = (famille === 'sinusoidale' || famille === 'tangente') && (cle === 'b' || cle === 'h')
            ? nombreOuPiV2(v)
            : nombre(v);
        if (n === null || Math.abs(n) > BORNE_COORDONNEE)
            d(`paramètre « ${k} » : pas un nombre`);
        else
            lus.set(cle, n);
    }
    /* LE QUOTIENT de deux polynômes du premier degré, (n1·x + n0) / (d1·x + d0) : la
       même courbe qu'une forme canonique, où il est converti. */
    if (famille === 'rationnelle' && ['n1', 'n0', 'd1', 'd0'].some((k) => lus.has(k))) {
        if (['a', 'b', 'h', 'k'].some((k) => lus.has(k))) {
            d('forme canonique (a, b, h, k) et quotient (n1, n0, d1, d0) mêlés');
            return { griefs };
        }
        const [n1, n0, d1, d0] = ['n1', 'n0', 'd1', 'd0'].map((k) => lus.get(k) ?? 0);
        if (d1 === 0) {
            d('d1 = 0 : le dénominateur ne dépend pas de x, ce n’est pas une fonction rationnelle');
            return { griefs };
        }
        const reste = n0 - (n1 * d0) / d1;
        if (Math.abs(reste) < 1e-12) {
            d('le numérateur est un multiple du dénominateur : la fonction est constante');
            return { griefs };
        }
        for (const k of ['n1', 'n0', 'd1', 'd0'])
            lus.delete(k);
        lus.set('a', reste);
        lus.set('b', d1);
        lus.set('h', -d0 / d1);
        lus.set('k', n1 / d1);
    }
    /* Un logarithme s'écrit par sa base : « log₂ x » a un facteur 1 qu'on n'écrit pas. */
    const a = lus.get('a') ??
        (famille === 'logarithmique' || famille === 'sinusoidale' || famille === 'tangente'
            ? 1
            : undefined);
    if (a === undefined) {
        d('paramètre « a » manquant');
        return { griefs };
    }
    const val = (k, neutre) => lus.get(k) ?? neutre;
    let fonction;
    switch (famille) {
        case 'lineaire':
            fonction = { famille, a, b: val('b', 0) };
            break;
        case 'quadratique': {
            if (a === 0)
                d("a = 0 : ce n'est pas une quadratique");
            const generale = lus.has('b') || lus.has('c');
            const canonique = lus.has('h') || lus.has('k');
            if (generale && canonique) {
                d('forme générale (b, c) et forme canonique (h, k) mêlées');
                return { griefs };
            }
            if (canonique) {
                const h = val('h', 0);
                fonction = { famille, a, b: -2 * a * h, c: a * h * h + val('k', 0) };
            }
            else {
                fonction = { famille, a, b: val('b', 0), c: val('c', 0) };
            }
            break;
        }
        case 'exponentielle': {
            const base = lus.get('b');
            if (base === undefined)
                d('base « b » manquante');
            else if (!(base > 0) || base === 1)
                d(`base ${formaterNombre(base)} : il faut b > 0 et b ≠ 1`);
            if (a === 0)
                d('a = 0 : la fonction est nulle');
            fonction = { famille, a, b: base ?? 2, c: val('c', 0) };
            break;
        }
        case 'logarithmique': {
            const base = lus.get('c');
            if (base === undefined)
                d('base « c » manquante');
            else if (!(base > 0) || base === 1)
                d(`base ${formaterNombre(base)} : il faut c > 0 et c ≠ 1`);
            const bi = val('b', 1);
            if (bi === 0)
                d('b = 0 : le logarithme de zéro n’existe pas');
            if (a === 0)
                d('a = 0 : la fonction est constante');
            fonction = { famille, a, b: bi, c: base ?? 2, h: val('h', 0), k: val('k', 0) };
            break;
        }
        case 'rationnelle': {
            const bi = val('b', 1);
            if (bi === 0)
                d('b = 0 : le dénominateur est nul partout');
            if (a === 0)
                d('a = 0 : la fonction est constante');
            fonction = { famille, a, b: bi, h: val('h', 0), k: val('k', 0) };
            break;
        }
        case 'sinusoidale': {
            const bi = val('b', 1);
            if (bi === 0)
                d('b = 0 : la fonction est constante, elle n’a plus de période');
            if (a === 0)
                d('a = 0 : la fonction est constante');
            fonction = { famille, fonction: trigo, a, b: bi, h: val('h', 0), k: val('k', 0) };
            break;
        }
        case 'tangente': {
            const bi = val('b', 1);
            if (bi === 0)
                d('b = 0 : la fonction est constante, elle n’a plus de période');
            if (a === 0)
                d('a = 0 : la fonction est constante');
            fonction = { famille, a, b: bi, h: val('h', 0), k: val('k', 0) };
            break;
        }
        default: {
            const bi = val('b', 1);
            if (bi === 0)
                d('b = 0 : la fonction est constante');
            if (a === 0)
                d('a = 0 : la fonction est constante');
            fonction = { famille, a, b: bi, h: val('h', 0), k: val('k', 0) };
        }
    }
    let domaine = null;
    if (domaineBrut !== undefined) {
        const d0 = Array.isArray(domaineBrut) ? nombre(domaineBrut[0]) : null;
        const d1 = Array.isArray(domaineBrut) ? nombre(domaineBrut[1]) : null;
        if (!Array.isArray(domaineBrut) ||
            domaineBrut.length !== 2 ||
            d0 === null ||
            d1 === null ||
            !(d0 < d1)) {
            d('domaine : il faut [début, fin] avec début < fin');
        }
        else
            domaine = [d0, d1];
    }
    return griefs.length > 0 ? { griefs } : { fonction, domaine };
}
function normaliserFonction(b, repere, quoi, dit) {
    const fb = champ(b, 'famille');
    const famille = familleFonctionV2(fb);
    if (famille === null) {
        dit(`${quoi} : famille « ${String(fb)} » inconnue (${FAMILLES_FONCTION_V2.join(', ')})`);
        return null;
    }
    const pb = champ(b, 'parametres');
    if (!estObjet(pb)) {
        dit(`${quoi} : paramètres absents`);
        return null;
    }
    const r = fonctionCanoniqueV2(famille, pb, champ(b, 'domaine'));
    if ('griefs' in r) {
        for (const q of r.griefs)
            dit(`${quoi} (${famille}) : ${q}`);
        return null;
    }
    if (repere !== null && tronconsFonction(r.fonction, r.domaine, repere).length === 0) {
        dit(`${quoi} (${famille}) : aucune portion de la courbe ne passe dans la fenêtre du repère`);
        return null;
    }
    /* L'asymptote verticale : une option du logarithme, la seule famille qui en a une. */
    const asymptote = champ(b, 'asymptote');
    if (asymptote !== undefined && asymptote !== true && asymptote !== false) {
        dit(`${quoi} (${famille}) : « asymptote » vaut vrai ou faux`);
        return null;
    }
    if (asymptote === true &&
        r.fonction.famille !== 'logarithmique' &&
        r.fonction.famille !== 'rationnelle' &&
        r.fonction.famille !== 'tangente') {
        dit(`${quoi} (${famille}) : seules les familles « logarithmique » et « rationnelle » tracent leurs asymptotes (et la « tangente »)`);
        return null;
    }
    /* Le style : la courbe pleine (son trait fort), ou la même en tirets. */
    const sb = champ(b, 'style');
    if (sb !== undefined && sb !== 'plein' && sb !== 'pointille') {
        dit(`${quoi} (${famille}) : style « ${String(sb)} » inconnu (plein, pointille)`);
        return null;
    }
    return {
        type: 'fonction',
        fonction: r.fonction,
        domaine: r.domaine,
        ...(asymptote === true ? { asymptote: true } : {}),
        ...(sb === 'pointille' ? { pointille: true } : {}),
    };
}
/** Une région : sa courbe-frontière (les familles et les paramètres de `fonction`), son côté, large ou stricte. */
function normaliserRegion(b, repere, quoi, dit) {
    const fb = champ(b, 'famille');
    const famille = familleFonctionV2(fb);
    if (famille === null) {
        dit(`${quoi} : famille « ${String(fb)} » inconnue (${FAMILLES_FONCTION_V2.join(', ')})`);
        return null;
    }
    const pb = champ(b, 'parametres');
    if (!estObjet(pb)) {
        dit(`${quoi} : paramètres absents`);
        return null;
    }
    if (champ(b, 'domaine') !== undefined) {
        dit(`${quoi} (${famille}) : une région n'a pas de « domaine » — elle n'est pas bornée en abscisse`);
        return null;
    }
    const f = fonctionCanoniqueV2(famille, pb);
    if ('griefs' in f) {
        for (const q of f.griefs)
            dit(`${quoi} (${famille}) : ${q}`);
        return null;
    }
    const cb = champ(b, 'cote');
    const cote = COTES_REGION_V2.find((c) => typeof cb === 'string' && cleNeutre(c) === cleNeutre(cb));
    if (cote === undefined) {
        dit(`${quoi} (${famille}) : côté « ${String(cb)} » inconnu (${COTES_REGION_V2.join(', ')})`);
        return null;
    }
    const sb = champ(b, 'stricte');
    if (sb !== undefined && typeof sb !== 'boolean') {
        dit(`${quoi} (${famille}) : « stricte » doit valoir true ou false`);
        return null;
    }
    if (repere !== null && !regionDansLaFenetre(f.fonction, cote, repere)) {
        dit(`${quoi} (${famille}) : rien de la région ne passe dans la fenêtre du repère`);
        return null;
    }
    return { type: 'region', fonction: f.fonction, cote, stricte: sb === true };
}
/**
 * LES PARTIES D'UNE FONCTION DÉFINIE PAR PARTIES (tranche T42), lues d'une liste
 * brute — ou ce qui cloche, sans préfixe (l'appelant pose le sien). La MÊME
 * lecture sert la figure (`par_parties`) et l'attendu d'un tracé
 * (`parties_egales`, `verifier.ts`) : la fonction dessinée et la fonction jugée
 * ne peuvent pas différer.
 *
 * Refusé : moins de deux parties ou plus de huit ; une partie sans `domaine` ou
 * sans ses deux `bouts` ; un bout plein ou vide là où la fonction n'existe pas ;
 * deux parties qui se CHEVAUCHENT ; deux bouts PLEINS au même x qui ne donnent
 * pas la même valeur — ce ne serait plus une fonction.
 */
export function lirePartiesV2(brut) {
    if (!Array.isArray(brut))
        return { griefs: ['« parties » : une liste de parties'] };
    const griefs = [];
    if (brut.length < PARTIES_MIN_V2 || brut.length > PARTIES_MAX_V2) {
        griefs.push(`${brut.length} partie(s) : de ${PARTIES_MIN_V2} à ${PARTIES_MAX_V2} (une seule : une « fonction » et son « domaine »)`);
    }
    const parties = [];
    brut.forEach((p, i) => {
        const quoi = `partie ${i + 1}`;
        if (!estObjet(p)) {
            griefs.push(`${quoi} : ce n'est pas un objet`);
            return;
        }
        const fb = champ(p, 'famille');
        const famille = familleFonctionV2(fb);
        if (famille === null) {
            griefs.push(`${quoi} : famille « ${String(fb)} » inconnue (${FAMILLES_FONCTION_V2.join(', ')})`);
            return;
        }
        const db = champ(p, 'domaine');
        if (db === undefined) {
            griefs.push(`${quoi} (${famille}) : « domaine » exigé — une partie est bornée`);
            return;
        }
        const r = fonctionCanoniqueV2(famille, champ(p, 'parametres'), db);
        if ('griefs' in r) {
            for (const q of r.griefs)
                griefs.push(`${quoi} (${famille}) : ${q}`);
            return;
        }
        const bb = champ(p, 'bouts');
        const bouts = Array.isArray(bb) ? bb.map((x) => BOUTS_V2.find((v) => v === x)) : [];
        const [g, d] = bouts;
        if (bouts.length !== 2 || g === undefined || d === undefined || r.domaine === null) {
            griefs.push(`${quoi} (${famille}) : « bouts » : deux valeurs parmi ${BOUTS_V2.join(', ')}`);
            return;
        }
        const domaine = r.domaine;
        for (const [x, bout] of [
            [domaine[0], g],
            [domaine[1], d],
        ]) {
            if (bout !== 'aucun' && !Number.isFinite(evaluerFonctionV2(r.fonction, x))) {
                griefs.push(`${quoi} (${famille}) : bout ${bout} en x = ${formaterNombre(x)}, où la fonction n'existe pas`);
            }
        }
        parties.push({ fonction: r.fonction, domaine, bouts: [g, d] });
    });
    if (griefs.length > 0)
        return { griefs };
    for (let i = 0; i < parties.length; i += 1) {
        for (let j = i + 1; j < parties.length; j += 1) {
            const p = parties[i];
            const q = parties[j];
            const de = Math.max(p.domaine[0], q.domaine[0]);
            const a = Math.min(p.domaine[1], q.domaine[1]);
            if (a - de > 1e-9) {
                griefs.push(`parties ${i + 1} et ${j + 1} : elles se chevauchent sur ]${formaterNombre(de)} ; ${formaterNombre(a)}[ — ce ne serait plus une fonction`);
                continue;
            }
            const conflit = boutsPleinsEnConflit(p, q);
            if (conflit !== null) {
                griefs.push(`parties ${i + 1} et ${j + 1} : deux bouts pleins en x = ${formaterNombre(conflit.x)}, ` +
                    `de valeurs ${formaterNombre(conflit.y1)} et ${formaterNombre(conflit.y2)} — ce ne serait plus une fonction ` +
                    '(l’un des deux est vide)');
            }
        }
    }
    return griefs.length > 0 ? { griefs } : { parties };
}
/**
 * Deux parties qui finissent au même x par deux bouts PLEINS de valeurs
 * différentes : ce n'est plus une fonction (deux images pour un même x).
 * `null` : rien de tel. Servi aussi au vérificateur, pour le tracé de l'élève.
 */
export function boutsPleinsEnConflit(p, q, egal = presDe) {
    for (const i of [0, 1]) {
        for (const j of [0, 1]) {
            if (p.bouts[i] !== 'plein' || q.bouts[j] !== 'plein')
                continue;
            const xp = p.domaine[i];
            const xq = q.domaine[j];
            if (!egal(xp, xq))
                continue;
            const y1 = evaluerFonctionV2(p.fonction, xp);
            const y2 = evaluerFonctionV2(q.fonction, xq);
            if (!egal(y1, y2))
                return { x: xp, y1, y2 };
        }
    }
    return null;
}
/** Les bornes MARQUÉES d'une fonction définie par parties : un point par bout plein ou vide ; un vide couvert par un plein est omis. */
export function bornesDesPartiesV2(parties) {
    const bornes = [];
    for (const partie of parties) {
        partie.bouts.forEach((bout, i) => {
            if (bout === 'aucun')
                return;
            const x = partie.domaine[i];
            const y = evaluerFonctionV2(partie.fonction, x);
            if (Number.isFinite(y))
                bornes.push({ p: { x, y }, creux: bout === 'vide' });
        });
    }
    const meme = (a, b) => presDe(a.x, b.x) && presDe(a.y, b.y);
    /* Un plein et un vide au même point : le point EST sur le graphique — plein.
       Deux fois la même borne : une fois. */
    return bornes.filter((b, i) => !(b.creux && bornes.some((c) => !c.creux && meme(b.p, c.p))) &&
        !bornes.slice(0, i).some((c) => c.creux === b.creux && meme(b.p, c.p)));
}
function normaliserParParties(b, repere, quoi, dit) {
    const r = lirePartiesV2(champ(b, 'parties'));
    if ('griefs' in r) {
        for (const q of r.griefs)
            dit(`${quoi} : ${q}`);
        return null;
    }
    if (repere !== null &&
        r.parties.every((p) => tronconsFonction(p.fonction, p.domaine, repere).length === 0)) {
        dit(`${quoi} : aucune partie ne passe dans la fenêtre du repère`);
        return null;
    }
    const sb = champ(b, 'style');
    if (sb !== undefined && sb !== 'plein' && sb !== 'pointille') {
        dit(`${quoi} : style « ${String(sb)} » inconnu (plein, pointille)`);
        return null;
    }
    return {
        type: 'par_parties',
        parties: r.parties,
        ...(sb === 'pointille' ? { pointille: true } : {}),
    };
}
/**
 * Les BOUTS des traits de la figure (tranche T42) : extrémités des segments,
 * bouts d'une `fonction` bornée par son domaine. Un point sans nom n'est permis
 * que là — une borne —, à 10⁻⁶ près : l'auteur écrit f(3) = 6, le calcul peut
 * donner 5,999999999.
 */
function boutsDesTraits(objets) {
    const bouts = [];
    const bout = (f, x) => {
        const y = evaluerFonctionV2(f, x);
        if (Number.isFinite(y))
            bouts.push({ x, y });
    };
    for (const o of objets) {
        if (o.type === 'segment')
            bouts.push(o.de, o.a);
        else if (o.type === 'fonction' && o.domaine !== null) {
            bout(o.fonction, o.domaine[0]);
            bout(o.fonction, o.domaine[1]);
        }
        else if (o.type === 'par_parties') {
            for (const p of o.parties) {
                bout(p.fonction, p.domaine[0]);
                bout(p.fonction, p.domaine[1]);
            }
        }
    }
    return bouts;
}
/** Deux coordonnées égales à 10⁻⁶ près, relatif au-delà de 1. */
const presDe = (u, v) => Math.abs(u - v) <= 1e-6 * Math.max(1, Math.abs(u), Math.abs(v));
/** Positif : le point (x, y) de la FIGURE est dans la région ; `NaN` là où la courbe n'existe pas. */
function dansLaRegion(f, cote, x, y) {
    const fx = evaluerFonctionV2(f, x);
    if (!Number.isFinite(fx))
        return Number.NaN;
    return cote === 'dessus' ? y - fx : fx - y;
}
/** Un point au moins de la fenêtre est-il dans la région ? Sondée sur une grille serrée. */
function regionDansLaFenetre(f, cote, r) {
    const N = 60;
    for (let i = 0; i <= N; i += 1) {
        const x = r.xMin + ((r.xMax - r.xMin) * i) / N;
        const fx = evaluerFonctionV2(f, x);
        if (!Number.isFinite(fx))
            continue;
        if (cote === 'dessus' ? fx < r.yMax : fx > r.yMin)
            return true;
    }
    return false;
}
/* --------------------------------------------------------------- espace */
/** Ce qu'une cote peut mesurer, par famille. Un rayon de cube n'existe pas : c'est un grief. */
export const GRANDEURS_PAR_FAMILLE_V2 = {
    cube: ['arete', 'diagonale_base'],
    prisme_droit: ['hauteur', 'cote_base', 'profondeur_base', 'diagonale_base'],
    pyramide_droite: ['hauteur', 'cote_base', 'profondeur_base', 'diagonale_base', 'apotheme'],
    cylindre: ['hauteur', 'rayon', 'diametre'],
    cone: ['hauteur', 'rayon', 'diametre', 'apotheme'],
    boule: ['rayon', 'diametre'],
};
/** Les clés de dimension acceptées, ramenées à leur nom canonique. `cote` dépend de la famille. */
const DIMENSION_PAR_CLE = {
    arete: 'arete',
    cote: 'cote',
    cotebase: 'coteBase',
    longueur: 'coteBase',
    longueurbase: 'coteBase',
    profondeurbase: 'profondeurBase',
    profondeur: 'profondeurBase',
    largeur: 'profondeurBase',
    largeurbase: 'profondeurBase',
    hauteur: 'hauteur',
    rayon: 'rayon',
    diametre: 'diametre',
    apotheme: 'apotheme',
};
const GRANDEUR_PAR_CLE = {
    hauteur: 'hauteur',
    rayon: 'rayon',
    diametre: 'diametre',
    apotheme: 'apotheme',
    arete: 'arete',
    cotebase: 'cote_base',
    profondeurbase: 'profondeur_base',
    largeurbase: 'profondeur_base',
    diagonalebase: 'diagonale_base',
};
/** Deux écritures d'une même longueur doivent dire la même chose (à 10⁻⁶ près, relatif). */
const accord = (u, v) => Math.abs(u - v) <= 1e-6 * Math.max(1, u, v);
function normaliserEspace(o, taille, dit) {
    const s = champ(o, 'solide');
    if (!estObjet(s)) {
        dit('solide : absent');
        return null;
    }
    const fb = champ(s, 'famille');
    const famille = FAMILLES_SOLIDE_V2.find((f) => typeof fb === 'string' && cleNeutre(f) === cleNeutre(fb));
    if (famille === undefined) {
        dit(`solide : famille « ${String(fb)} » inconnue (${FAMILLES_SOLIDE_V2.join(', ')})`);
        return null;
    }
    const dims = champ(s, 'dimensions');
    if (!estObjet(dims) || Object.keys(dims).length === 0) {
        dit(`solide (${famille}) : sans dimensions`);
        return null;
    }
    const n0 = { n: 0 };
    const d = (q) => {
        n0.n += 1;
        dit(`solide (${famille}) : ${q}`);
    };
    const lues = new Map();
    for (const [k, v] of Object.entries(dims)) {
        const cle = cleNeutre(k);
        if (cle === 'base')
            continue;
        let canon = DIMENSION_PAR_CLE[cle];
        if (canon === 'cote')
            canon = famille === 'cube' ? 'arete' : 'coteBase';
        if (canon === undefined) {
            d(`dimension « ${k} » inconnue`);
            continue;
        }
        const n = nombre(v);
        if (n === null || !(n > 0) || n > BORNE_COORDONNEE) {
            d(`dimension « ${k} » : ${JSON.stringify(v) ?? 'rien'} n'est pas une longueur positive`);
            continue;
        }
        const deja = lues.get(canon);
        if (deja !== undefined && !accord(deja, n))
            d(`dimension « ${canon} » écrite deux fois, différemment`);
        lues.set(canon, n);
    }
    const permises = {
        cube: ['arete'],
        prisme_droit: ['coteBase', 'profondeurBase', 'hauteur'],
        pyramide_droite: ['coteBase', 'profondeurBase', 'hauteur'],
        cylindre: ['rayon', 'diametre', 'hauteur'],
        cone: ['rayon', 'diametre', 'hauteur', 'apotheme'],
        boule: ['rayon', 'diametre'],
    };
    for (const k of lues.keys()) {
        if (!permises[famille].includes(k))
            d(`dimension « ${k} » sans objet pour ce solide`);
    }
    const exige = (k) => {
        const v = lues.get(k);
        if (v === undefined)
            d(`dimension « ${k} » manquante`);
        return v ?? 1;
    };
    /** Le rayon, écrit comme tel ou par le diamètre — deux emballages du même contenu. */
    const rayon = () => {
        const r = lues.get('rayon');
        const diam = lues.get('diametre');
        if (r !== undefined && diam !== undefined && !accord(2 * r, diam)) {
            d(`rayon ${formaterNombre(r)} et diamètre ${formaterNombre(diam)} incompatibles`);
        }
        if (r === undefined && diam === undefined)
            d('dimension « rayon » (ou « diametre ») manquante');
        return r ?? (diam !== undefined ? diam / 2 : 1);
    };
    let solide;
    switch (famille) {
        case 'cube':
            solide = { famille, arete: exige('arete') };
            break;
        case 'prisme_droit':
        case 'pyramide_droite': {
            const bb = champ(s, 'base') ?? champ(dims, 'base');
            const base = BASES_SOLIDE.find((x) => typeof bb === 'string' && cleNeutre(x) === cleNeutre(bb));
            if (base === undefined)
                d(`base « ${String(bb)} » inconnue ou absente (${BASES_SOLIDE.join(', ')})`);
            const profondeur = lues.get('profondeurBase');
            if (base === 'rectangle' && profondeur === undefined) {
                d('une base rectangulaire exige « profondeurBase » (son second côté)');
            }
            if (base !== undefined && base !== 'rectangle' && profondeur !== undefined) {
                d(`« profondeurBase » donnée pour une base ${base}, qui n'en a pas`);
            }
            solide = {
                famille,
                base: base ?? 'carre',
                coteBase: exige('coteBase'),
                profondeurBase: base === 'rectangle' ? (profondeur ?? null) : null,
                hauteur: exige('hauteur'),
            };
            break;
        }
        case 'cylindre':
            solide = { famille, rayon: rayon(), hauteur: exige('hauteur') };
            break;
        case 'cone': {
            const r = rayon();
            const h = lues.get('hauteur');
            const ap = lues.get('apotheme');
            let hauteur = h ?? 1;
            /* Un cône se décrit aussi par son rayon et son apothème : même solide,
               autre emballage. Donnés tous les trois, ils doivent s'accorder. */
            if (h === undefined && ap !== undefined) {
                if (!(ap > r))
                    d(`apothème ${formaterNombre(ap)} pas plus long que le rayon`);
                else
                    hauteur = Math.sqrt(ap * ap - r * r);
            }
            else if (h === undefined) {
                d('dimension « hauteur » (ou « apotheme ») manquante');
            }
            else if (ap !== undefined && !accord(ap * ap, r * r + h * h)) {
                d(`apothème ${formaterNombre(ap)} incompatible avec rayon et hauteur`);
            }
            solide = { famille, rayon: r, hauteur };
            break;
        }
        case 'boule':
            solide = { famille, rayon: rayon() };
            break;
    }
    const cotes = [];
    const cb = champ(o, 'cotes');
    if (cb !== undefined && !Array.isArray(cb))
        d('« cotes » doit être une liste');
    if (Array.isArray(cb)) {
        const vues = new Set();
        cb.forEach((c, i) => {
            const quoi = `cote ${i + 1}`;
            if (!estObjet(c)) {
                d(`${quoi} : ce n'est pas un objet`);
                return;
            }
            const gb = champ(c, 'grandeur');
            const cle = typeof gb === 'string' ? cleNeutre(gb) : '';
            const grandeur = cle === 'cote' ? (famille === 'cube' ? 'arete' : 'cote_base') : GRANDEUR_PAR_CLE[cle];
            const texte = texteAffiche(champ(c, 'texte'), `solide (${famille}), ${quoi}`, dit);
            if (grandeur === undefined || !GRANDEURS_PAR_FAMILLE_V2[famille].includes(grandeur)) {
                d(`${quoi} : un ${famille} n'a pas de « ${String(gb)} » à coter`);
                return;
            }
            const base = 'base' in solide ? solide.base : null;
            if (grandeur === 'profondeur_base' && base !== 'rectangle') {
                d(`${quoi} : « profondeur_base » n'existe que pour une base rectangulaire`);
                return;
            }
            if (grandeur === 'diagonale_base' &&
                famille !== 'cube' &&
                base !== 'carre' &&
                base !== 'rectangle') {
                d(`${quoi} : « diagonale_base » n'est définie que pour une base carrée ou rectangulaire`);
                return;
            }
            if (grandeur === 'apotheme' && base === 'rectangle') {
                d(`${quoi} : une pyramide à base rectangulaire a DEUX apothèmes — lequel ?`);
                return;
            }
            if (vues.has(grandeur)) {
                d(`${quoi} : « ${grandeur} » cotée deux fois`);
                return;
            }
            vues.add(grandeur);
            if (texte !== null)
                cotes.push({ grandeur, texte });
        });
    }
    let vue = VUE_PAR_DEFAUT_V2;
    const vb = champ(o, 'vue');
    if (vb !== undefined) {
        const az = estObjet(vb) ? nombre(champ(vb, 'azimut')) : null;
        const el = estObjet(vb) ? nombre(champ(vb, 'elevation')) : null;
        if (az === null || el === null)
            d('vue : il faut « azimut » et « elevation », en degrés');
        else
            vue = vueBornee({ azimut: az, elevation: el });
    }
    if (n0.n > 0)
        return null;
    return { espace: 'espace', taille, solide, cotes, vue };
}
/**
 * Une vue ramenée dans son domaine : azimut dans [0, 360), élévation dans
 * [−89, 89]. L'élève peut tourner la figure autant qu'il veut ; le dessin, lui,
 * ne reçoit jamais une vue dégénérée.
 */
export function vueBornee(v) {
    const az = Number.isFinite(v.azimut) ? ((v.azimut % 360) + 360) % 360 : VUE_PAR_DEFAUT_V2.azimut;
    const el = Number.isFinite(v.elevation)
        ? Math.max(-ELEVATION_LIMITE_V2, Math.min(ELEVATION_LIMITE_V2, v.elevation))
        : VUE_PAR_DEFAUT_V2.elevation;
    return { azimut: az, elevation: el };
}
/* ============================================================== géométrie */
/** L'aire signée d'un polygone (formule du lacet) : positive dans le sens antihoraire. */
export function aireSignee(p) {
    let s = 0;
    for (let i = 0; i < p.length; i += 1) {
        const a = p[i];
        const b = p[(i + 1) % p.length];
        s += a.x * b.y - b.x * a.y;
    }
    return s / 2;
}
/** L'angle géométrique (0..180°) en `s` entre `s→p` et `s→q`. */
export function mesureAngle(s, p, q) {
    const u = moins(p, s);
    const v = moins(q, s);
    const c = (u.x * v.x + u.y * v.y) / (norme(u) * norme(v));
    return (Math.acos(Math.max(-1, Math.min(1, c))) * 180) / Math.PI;
}
const borne = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
/** Efface le bruit flottant d'une valeur calculée par pas (0,1 + 0,2). */
const propre = (v) => Number(v.toFixed(10));
/** L'image de x par une fonction de la famille fermée — jamais une expression libre. */
export function evaluerFonctionV2(f, x) {
    switch (f.famille) {
        case 'lineaire':
            return f.a * x + f.b;
        case 'quadratique':
            return f.a * x * x + f.b * x + f.c;
        case 'exponentielle':
            return f.a * Math.pow(f.b, x) + f.c;
        case 'valeur_absolue':
            return f.a * Math.abs(f.b * (x - f.h)) + f.k;
        case 'racine': {
            const u = f.b * (x - f.h);
            return u < 0 ? Number.NaN : f.a * Math.sqrt(u) + f.k;
        }
        case 'logarithmique': {
            const u = f.b * (x - f.h);
            return u > 0 ? (f.a * Math.log(u)) / Math.log(f.c) + f.k : Number.NaN;
        }
        case 'rationnelle': {
            /* Elle n'existe pas en x = h : ni +∞ ni −∞, rien. */
            const u = f.b * (x - f.h);
            return u === 0 ? Number.NaN : f.a / u + f.k;
        }
        case 'sinusoidale': {
            const u = f.b * (x - f.h);
            return f.a * (f.fonction === 'cos' ? Math.cos(u) : Math.sin(u)) + f.k;
        }
        case 'tangente': {
            /* Elle n'existe pas sur ses asymptotes (cos u = 0, à la précision du calcul). */
            const u = f.b * (x - f.h);
            const c = Math.cos(u);
            return Math.abs(c) < 1e-12 ? Number.NaN : (f.a * Math.sin(u)) / c + f.k;
        }
    }
}
/** Les asymptotes x = h + π/(2|b|) + nπ/|b| d'une tangente, strictement dans ]lo ; hi[. */
export function asymptotesTangenteV2(f, lo, hi) {
    if (f.b === 0)
        return [];
    const P = Math.PI / Math.abs(f.b);
    const premiere = f.h + P / 2 + Math.ceil((lo - (f.h + P / 2)) / P - 1e-9) * P;
    const sortie = [];
    for (let x = premiere; x < hi - 1e-9 && sortie.length < 400; x += P)
        if (x > lo + 1e-9)
            sortie.push(x);
    return sortie;
}
/** La période d'une sinusoïde, 2π/|b|. */
export function periodeSinusoideV2(f) {
    return (2 * Math.PI) / Math.abs(f.b);
}
const ECHANTILLONS_FONCTION = 240;
/** L'écart, à l'écran, entre un arc et la longueur écrite le long de lui. */
const ECART_TEXTE_ARC = 4;
/**
 * Un ARC en style `fort` est l'arc que la figure DÉSIGNE, souvent posé sur son
 * propre cercle : un quart de trait en plus (le `fort` d'une courbe) ne s'y
 * verrait pas. Les composants multiplient son épaisseur par ce facteur.
 */
export const FACTEUR_ARC_DESIGNE_V2 = 1.7;
/**
 * Les portions VISIBLES d'une courbe : échantillonnée sur domaine ∩ fenêtre,
 * coupée EXACTEMENT au bord haut ou bas de la fenêtre (le point de sortie est
 * cherché par dichotomie sur la fonction elle-même, pas interpolé entre deux
 * échantillons). Les abscisses remarquables — sommet d'une parabole, pointe
 * d'une valeur absolue, origine d'une racine — sont ajoutées aux
 * échantillons : un sommet raté de peu dessinerait une parabole émoussée.
 */
function tronconsFonction(f, domaine, r) {
    if (f.famille === 'tangente') {
        /* UNE TANGENTE A UNE BRANCHE ENTRE DEUX ASYMPTOTES : chacune est échantillonnée
           À PART, d'un point collé à une asymptote à un point collé à la suivante, les
           abscisses resserrées à leur approche — dix par décade, des deux côtés. Rien ne
           relie jamais deux branches : aucun trait ne traverse une asymptote. */
        const xa = Math.max(domaine?.[0] ?? r.xMin, r.xMin);
        const xb = Math.min(domaine?.[1] ?? r.xMax, r.xMax);
        if (!(xb > xa))
            return [];
        const colle = Math.abs(r.xMax - r.xMin) * 1e-9;
        const poles = asymptotesTangenteV2(f, xa, xb);
        const bornes = [xa, ...poles, xb];
        const sortie = [];
        for (let i = 0; i < bornes.length - 1; i += 1) {
            const gauchePole = i > 0;
            const droitePole = i < bornes.length - 2;
            const x0 = bornes[i] + (gauchePole ? colle : 0);
            const x1 = bornes[i + 1] - (droitePole ? colle : 0);
            if (!(x1 > x0))
                continue;
            const loin = x1 - x0;
            const remarquables = [];
            for (let k = 1; k <= 90; k += 1) {
                const ecart = loin * Math.pow(10, -k / 10);
                if (gauchePole)
                    remarquables.push(bornes[i] + ecart);
                if (droitePole)
                    remarquables.push(bornes[i + 1] - ecart);
            }
            sortie.push(...tronconsSur(f, x0, x1, remarquables, r));
        }
        return sortie;
    }
    if (f.famille === 'rationnelle') {
        /* UNE FONCTION RATIONNELLE A DEUX BRANCHES, de part et d'autre de son asymptote
           verticale x = h : chacune est échantillonnée À PART, jusqu'à un point collé à
           l'asymptote (la courbe y est déjà hors de la fenêtre, donc coupée à son bord).
           Rien ne relie jamais l'une à l'autre : aucun trait ne traverse l'asymptote. */
        const xa = Math.max(domaine?.[0] ?? r.xMin, r.xMin);
        const xb = Math.min(domaine?.[1] ?? r.xMax, r.xMax);
        const colle = Math.abs(r.xMax - r.xMin) * 1e-9;
        return [
            ...brancheDeFonction(f, xa, Math.min(xb, f.h - colle), r, f.h),
            ...brancheDeFonction(f, Math.max(xa, f.h + colle), xb, r, f.h),
        ];
    }
    let x0 = Math.max(domaine?.[0] ?? r.xMin, r.xMin);
    let x1 = Math.min(domaine?.[1] ?? r.xMax, r.xMax);
    const remarquables = [];
    if (f.famille === 'logarithmique') {
        /* UN LOGARITHME N'EXISTE QUE D'UN CÔTÉ DE SON ASYMPTOTE x = h, et il y plonge :
           on n'échantillonne que ce côté, à partir d'un point collé à l'asymptote (la
           courbe y est déjà hors de la fenêtre, donc coupée exactement à son bord par
           la dichotomie plus bas), et on resserre les abscisses à son approche — dix
           par décade — pour que le coude ne soit pas une ligne brisée. Rien n'est
           jamais tracé de l'autre côté, ni aucun segment vertical à l'asymptote. */
        const colle = Math.abs(r.xMax - r.xMin) * 1e-9;
        if (f.b > 0)
            x0 = Math.max(x0, f.h + colle);
        else
            x1 = Math.min(x1, f.h - colle);
        const loin = f.b > 0 ? x1 - f.h : f.h - x0;
        for (let i = 1; i <= 90 && loin > 0; i += 1) {
            const ecart = loin * Math.pow(10, -i / 10);
            remarquables.push(f.b > 0 ? f.h + ecart : f.h - ecart);
        }
    }
    if (f.famille === 'quadratique' && f.a !== 0)
        remarquables.push(-f.b / (2 * f.a));
    if (f.famille === 'valeur_absolue' || f.famille === 'racine')
        remarquables.push(f.h);
    if (f.famille === 'sinusoidale' && f.b !== 0) {
        /* LES SOMMETS, LES CREUX ET LES PASSAGES SUR L'AXE d'une sinusoïde — chaque quart
           de période : un sommet raté de peu dessinerait une vague émoussée, et une
           période courte (b grand) n'aurait que quelques échantillons réguliers. */
        const quart = periodeSinusoideV2(f) / 4;
        const n0 = Math.ceil((x0 - f.h) / quart);
        const n1 = Math.floor((x1 - f.h) / quart);
        for (let n = n0; n <= n1 && n - n0 <= 4 * ECHANTILLONS_FONCTION; n += 1)
            remarquables.push(f.h + n * quart);
    }
    return tronconsSur(f, x0, x1, remarquables, r);
}
/**
 * Une branche d'une fonction rationnelle, sur [x0 ; x1], du côté de l'asymptote
 * x = `pole` où elle se trouve : les abscisses se resserrent à son approche —
 * dix par décade — pour que le coude ne soit pas une ligne brisée.
 */
function brancheDeFonction(f, x0, x1, r, pole) {
    if (!(x1 > x0))
        return [];
    const aDroite = x0 >= pole;
    const loin = aDroite ? x1 - pole : pole - x0;
    const remarquables = [];
    for (let i = 1; i <= 90 && loin > 0; i += 1) {
        const ecart = loin * Math.pow(10, -i / 10);
        remarquables.push(aDroite ? pole + ecart : pole - ecart);
    }
    return tronconsSur(f, x0, x1, remarquables, r);
}
/** Les portions visibles de la courbe sur [x0 ; x1], où elle est d'UN SEUL tenant. */
function tronconsSur(f, x0, x1, remarquables, r) {
    if (!(x1 > x0))
        return [];
    const xs = [];
    for (let i = 0; i <= ECHANTILLONS_FONCTION; i += 1)
        xs.push(x0 + ((x1 - x0) * i) / ECHANTILLONS_FONCTION);
    for (const x of remarquables)
        if (x > x0 && x < x1)
            xs.push(x);
    xs.sort((a, b) => a - b);
    const g = (x) => evaluerFonctionV2(f, x);
    const dedans = (y) => Number.isFinite(y) && y >= r.yMin && y <= r.yMax;
    /** Abscisse où `g` franchit `cible`, entre `xa` (côté a) et `xb` (côté b). */
    const franchir = (xa, xb, cible) => {
        let a = xa;
        let b = xb;
        const signeA = Math.sign(g(xa) - cible);
        for (let i = 0; i < 60; i += 1) {
            const m = (a + b) / 2;
            const v = g(m);
            if (Number.isFinite(v) && Math.sign(v - cible) === signeA)
                a = m;
            else
                b = m;
        }
        return (a + b) / 2;
    };
    const bordDe = (y) => !Number.isFinite(y) ? null : y > r.yMax ? r.yMax : y < r.yMin ? r.yMin : null;
    const troncons = [];
    let courant = [];
    const clore = () => {
        if (courant.length >= 2)
            troncons.push(courant);
        courant = [];
    };
    let xp = xs[0];
    let yp = g(xp);
    if (dedans(yp))
        courant.push({ x: xp, y: yp });
    for (let i = 1; i < xs.length; i += 1) {
        const x = xs[i];
        const y = g(x);
        if (x === xp)
            continue;
        const pin = dedans(yp);
        const cin = dedans(y);
        if (pin && cin)
            courant.push({ x, y });
        else if (pin && !cin) {
            const b = bordDe(y);
            if (b !== null)
                courant.push({ x: franchir(xp, x, b), y: b });
            clore();
        }
        else if (!pin && cin) {
            const b = bordDe(yp);
            courant = b !== null ? [{ x: franchir(xp, x, b), y: b }] : [];
            courant.push({ x, y });
        }
        else {
            /* Deux échantillons dehors, de part et d'autre de la fenêtre : la courbe
               la traverse entre les deux (pente très forte). */
            const bp = bordDe(yp);
            const bc = bordDe(y);
            if (bp !== null && bc !== null && bp !== bc) {
                troncons.push([
                    { x: franchir(xp, x, bp), y: bp },
                    { x: franchir(xp, x, bc), y: bc },
                ]);
            }
        }
        xp = x;
        yp = y;
    }
    clore();
    return troncons;
}
/** Le segment [p ; q] coupé par la fenêtre (Liang-Barsky) : ses deux bouts, et où ils tombent sur lui (0 à 1). */
function couperSegment(p, q, r) {
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    let t0 = 0;
    let t1 = 1;
    for (const [pente, marge] of [
        [-dx, p.x - r.xMin],
        [dx, r.xMax - p.x],
        [-dy, p.y - r.yMin],
        [dy, r.yMax - p.y],
    ]) {
        if (pente === 0) {
            if (marge < 0)
                return null;
            continue;
        }
        const t = marge / pente;
        if (pente < 0) {
            if (t > t1)
                return null;
            if (t > t0)
                t0 = t;
        }
        else {
            if (t < t0)
                return null;
            if (t < t1)
                t1 = t;
        }
    }
    return {
        de: { x: p.x + t0 * dx, y: p.y + t0 * dy },
        a: { x: p.x + t1 * dx, y: p.y + t1 * dy },
        t0,
        t1,
    };
}
/** Une ligne brisée coupée par la fenêtre : ses morceaux visibles, chacun d'un seul tenant. */
function couperPolyligne(ligne, r) {
    const morceaux = [];
    let courant = [];
    const clore = () => {
        if (courant.length >= 2)
            morceaux.push(courant);
        courant = [];
    };
    for (let i = 1; i < ligne.length; i += 1) {
        const c = couperSegment(ligne[i - 1], ligne[i], r);
        if (c === null || !(c.t1 > c.t0)) {
            clore();
            continue;
        }
        if (c.t0 > 0 || courant.length === 0) {
            clore();
            courant = [c.de];
        }
        courant.push(c.a);
        if (c.t1 < 1)
            clore();
    }
    clore();
    return morceaux;
}
const ECHANTILLONS_CONIQUE = 180;
/**
 * Les portions VISIBLES d'une ellipse, d'une hyperbole ou d'une parabole — par
 * leur PARAMÉTRAGE, pas par une fonction de x : la courbe est parcourue d'un
 * mouvement, serrée là où elle tourne (ses sommets), puis coupée par la fenêtre.
 *
 *  - l'ellipse : un tour, fermé ; un seul morceau quand elle tient dans la fenêtre ;
 *  - l'hyperbole : ses DEUX branches, chacune parcourue à part jusqu'au-delà de la
 *    fenêtre — rien ne relie jamais l'une à l'autre ;
 *  - la parabole : de part et d'autre de son sommet, jusqu'au-delà de la fenêtre.
 */
function tronconsConique(o, r) {
    const N = ECHANTILLONS_CONIQUE;
    const centre = o.type === 'parabole' ? o.sommet : o.centre;
    /* Jusqu'où la fenêtre s'étend, de part et d'autre du centre (ou du sommet). */
    const Dx = Math.max(Math.abs(r.xMin - centre.x), Math.abs(r.xMax - centre.x));
    const Dy = Math.max(Math.abs(r.yMin - centre.y), Math.abs(r.yMax - centre.y));
    if (o.type === 'ellipse') {
        const tour = [];
        for (let i = 0; i <= N; i += 1) {
            const t = (2 * Math.PI * (i % N)) / N;
            tour.push({ x: centre.x + o.a * Math.cos(t), y: centre.y + o.b * Math.sin(t) });
        }
        const morceaux = couperPolyligne(tour, r);
        /* Coupée par la fenêtre, elle peut l'être au milieu d'un arc visible qui passe
           par le point de départ du tour : ses deux moitiés se recollent. */
        const premier = morceaux[0];
        const dernier = morceaux[morceaux.length - 1];
        if (premier !== undefined &&
            dernier !== undefined &&
            morceaux.length > 1 &&
            memePoint(premier[0], tour[0]) &&
            memePoint(dernier[dernier.length - 1], tour[0])) {
            morceaux[0] = [...dernier, ...premier.slice(1)];
            morceaux.pop();
        }
        return morceaux;
    }
    if (o.type === 'hyperbole') {
        const horizontale = o.ouverture === 'horizontale';
        /* Le long de l'axe des sommets : `p` (a ou b) et l'étendue `Dp` ; en travers : `q`, `Dq`. */
        const [p, Dp, q, Dq] = horizontale ? [o.a, Dx, o.b, Dy] : [o.b, Dy, o.a, Dx];
        if (!(Dp > p))
            return [];
        /* Le paramètre où la branche sort de la fenêtre, d'un côté ou de l'autre — un rien au-delà. */
        const T = 1.02 * Math.min(Math.acosh(Dp / p), Math.asinh(Dq / q)) + 1e-6;
        const branches = [];
        for (const s of [-1, 1]) {
            const branche = [];
            for (let i = 0; i <= N; i += 1) {
                const t = -T + (2 * T * i) / N;
                const le = s * p * Math.cosh(t);
                const tr = q * Math.sinh(t);
                branche.push(horizontale
                    ? { x: centre.x + le, y: centre.y + tr }
                    : { x: centre.x + tr, y: centre.y + le });
            }
            branches.push(...couperPolyligne(branche, r));
        }
        return branches;
    }
    const horizontal = o.axe === 'horizontal';
    /* u : l'écart au sommet EN TRAVERS de l'axe ; le long de l'axe, u²/(4c). */
    const [Dle, Dtr] = horizontal ? [Dx, Dy] : [Dy, Dx];
    const U = 1.02 * Math.min(Dtr, Math.sqrt(4 * Math.abs(o.c) * Dle)) + 1e-6;
    const ligne = [];
    for (let i = 0; i <= N; i += 1) {
        const u = -U + (2 * U * i) / N;
        const le = (u * u) / (4 * o.c);
        ligne.push(horizontal ? { x: centre.x + le, y: centre.y + u } : { x: centre.x + u, y: centre.y + le });
    }
    return couperPolyligne(ligne, r);
}
/* ============================================================ échelle, axes */
/**
 * Les échelles x et y du plan (unités d'affichage par unité du repère).
 *
 * ÉGALES, sauf dans un repère à axes sans objet euclidien — voir l'en-tête,
 * règle 1. Une figure euclidienne très haute est plafonnée en hauteur ; très
 * large, elle reste une bande mince (une droite numérique EST une bande).
 */
function echellePlan(r, objets, toujoursEgale = false) {
    const lx = r.xMax - r.xMin;
    const ly = r.yMax - r.yMin;
    const ratio = ly / lx;
    const euclidien = toujoursEgale ||
        objets.some((o) => o.type === 'cercle' ||
            o.type === 'angle' ||
            o.type === 'cote' ||
            o.type === 'arc' ||
            o.type === 'secteur' ||
            /* Une conique se lit par ses distances (demi-axes, foyers, directrice) : étirée, elle mentirait. */
            o.type === 'ellipse' ||
            o.type === 'hyperbole' ||
            o.type === 'parabole');
    if (ratio >= RATIO_MIN && ratio <= RATIO_MAX) {
        const k = LARGEUR_REFERENCE_V2 / lx;
        return { kx: k, ky: k };
    }
    if (r.axes && !euclidien) {
        const kx = LARGEUR_REFERENCE_V2 / lx;
        return { kx, ky: (LARGEUR_REFERENCE_V2 * borne(ratio, RATIO_MIN, RATIO_MAX)) / ly };
    }
    const k = Math.min(LARGEUR_REFERENCE_V2 / lx, (LARGEUR_REFERENCE_V2 * RATIO_MAX) / ly);
    return { kx: k, ky: k };
}
/** Un pas « rond » (1, 2 ou 5 × 10ⁿ) qui donne environ huit intervalles. */
function pasRond(etendue) {
    const brut = etendue / 8;
    const puissance = 10 ** Math.floor(Math.log10(brut));
    const f = brut / puissance;
    const n = f < 1.5 ? 1 : f < 3.5 ? 2 : f < 7.5 ? 5 : 10;
    return n * puissance;
}
function valeursAuPas(origine, pas, lo, hi) {
    const k0 = Math.ceil((lo - origine) / pas - 1e-9);
    const k1 = Math.floor((hi - origine) / pas + 1e-9);
    if (k1 - k0 > 400)
        return [];
    const out = [];
    for (let k = k0; k <= k1; k += 1)
        out.push(propre(origine + k * pas));
    return out;
}
function etiquettesDe(objets) {
    return objets.filter((o) => o.type === 'etiquette');
}
/**
 * Un axe, ses graduations — et ce que la figure en a DÉJÀ écrit (en-tête,
 * règle 2). La « bande » d'un axe est la lisière de 10 % de la fenêtre sous
 * l'axe des x, ou à gauche de l'axe des y : là où l'on écrit des libellés.
 */
function analyserAxe(nomAxe, lo, hi, pasDonne, pasAuto, bande, griefs) {
    const numeriques = [];
    let libres = 0;
    for (const e of bande) {
        const v = nombre(e.texte);
        if (v !== null && Math.abs(v - e.position) <= 1e-6 * Math.max(1, Math.abs(v)))
            numeriques.push(v);
        else
            libres += 1;
    }
    if (numeriques.length >= 2) {
        const vals = [...new Set(numeriques)].sort((a, b) => a - b);
        const ecarts = vals.slice(1).map((v, i) => v - vals[i]);
        const premier = ecarts[0];
        const regulier = ecarts.every((e) => accord(e, premier));
        const pas = pasDonne ?? (regulier ? premier : Math.min(...ecarts));
        const origine = pasDonne !== null ? 0 : ((vals[0] % pas) + pas) % pas;
        const hors = vals.filter((v) => {
            const q = (v - origine) / pas;
            return Math.abs(q - Math.round(q)) > 1e-6;
        });
        if (hors.length > 0) {
            griefs.push(`repère : graduations écrites sur l'axe des ${nomAxe} (${vals.map(formaterNombre).join(' ; ')}) ` +
                `hors du pas ${formaterNombre(pas)}`);
        }
        const debut = Math.max(lo, vals[0]);
        const fin = Math.min(hi, vals[vals.length - 1]);
        return {
            valeurs: valeursAuPas(origine, pas, debut, fin),
            pas,
            libellesCalcules: false,
            bandeEcrite: true,
        };
    }
    if (libres >= 2)
        return { valeurs: [], pas: null, libellesCalcules: false, bandeEcrite: true };
    const pas = pasDonne ?? pasAuto;
    return { valeurs: valeursAuPas(0, pas, lo, hi), pas, libellesCalcules: true, bandeEcrite: false };
}
function analyserAxes(r, etiquettes, isotrope) {
    const lx = r.xMax - r.xMin;
    const ly = r.yMax - r.yMin;
    /* En échelle égale, un seul pas pour les deux axes : la grille reste carrée. */
    const autoX = isotrope ? pasRond(Math.max(lx, ly)) : pasRond(lx);
    const autoY = isotrope ? autoX : pasRond(ly);
    const griefs = [];
    if (!r.axes) {
        const px = r.pas?.x ?? autoX;
        const py = r.pas?.y ?? autoY;
        const libre = (p, lo, hi) => ({
            valeurs: valeursAuPas(0, p, lo, hi),
            pas: p,
            libellesCalcules: false,
            bandeEcrite: false,
        });
        return { x: libre(px, r.xMin, r.xMax), y: libre(py, r.yMin, r.yMax), griefs };
    }
    const x0 = borne(0, r.xMin, r.xMax);
    const y0 = borne(0, r.yMin, r.yMax);
    const bandeX = etiquettes
        .filter((e) => e.x >= r.xMin && e.x <= r.xMax && e.y < y0 - 1e-9 && e.y >= y0 - 0.1 * ly)
        .map((e) => ({ texte: e.texte, position: e.x }));
    const bandeY = etiquettes
        .filter((e) => e.y >= r.yMin && e.y <= r.yMax && e.x < x0 - 1e-9 && e.x >= x0 - 0.1 * lx)
        .map((e) => ({ texte: e.texte, position: e.y }));
    return {
        x: analyserAxe('x', r.xMin, r.xMax, r.pas?.x ?? null, autoX, bandeX, griefs),
        y: analyserAxe('y', r.yMin, r.yMax, r.pas?.y ?? null, autoY, bandeY, griefs),
        griefs,
    };
}
function trait(a, b, style, calque) {
    return { type: 'trait', x1: a.x, y1: a.y, x2: b.x, y2: b.y, style, calque };
}
/** La droite (p, q) coupée par la fenêtre — Liang-Barsky. `null` si elle ne la traverse pas. */
export function couperDroite(p, q, r) {
    return couperLigne(p, q, r, -Infinity);
}
/**
 * La demi-droite d'origine `p` passant par `q`, coupée par la fenêtre.
 * `null` si elle ne la traverse pas.
 */
export function couperDemiDroite(p, q, r) {
    return couperLigne(p, q, r, 0);
}
/** Liang-Barsky, le paramètre partant de `tMin` (−∞ : droite ; 0 : demi-droite). */
function couperLigne(p, q, r, tMin) {
    const d = moins(q, p);
    let t0 = tMin;
    let t1 = Infinity;
    const bords = [
        [-d.x, p.x - r.xMin],
        [d.x, r.xMax - p.x],
        [-d.y, p.y - r.yMin],
        [d.y, r.yMax - p.y],
    ];
    for (const [pp, qq] of bords) {
        if (Math.abs(pp) < 1e-12) {
            if (qq < 0)
                return null;
            continue;
        }
        const t = qq / pp;
        if (pp < 0)
            t0 = Math.max(t0, t);
        else
            t1 = Math.min(t1, t);
    }
    if (!(t1 > t0))
        return null;
    return [plus(p, fois(d, t0)), plus(p, fois(d, t1))];
}
const angleAff = (v) => Math.atan2(v.y, v.x);
const direction = (a) => ({ x: Math.cos(a), y: Math.sin(a) });
/**
 * La direction où poser le nom d'un point : au milieu du PLUS GRAND secteur
 * libre autour de lui. On relève la direction de chaque trait qui passe par le
 * point (un bout de segment en donne une, un point intérieur deux), de chaque
 * arc d'angle dont il est le sommet, et de chaque texte voisin ; le nom va
 * dans le plus large vide. C'est ce qui garantit qu'il ne tombe jamais sur un
 * trait, y compris sur un point intérieur d'une droite numérique. À égalité,
 * on s'éloigne du centre de la figure, puis on préfère le haut.
 */
function directionNom(p, index, directions, eloigne) {
    const EPS = 0.75;
    const VOISIN = 2.6 * TAILLE_POLICE_V2;
    const angles = directions.map(angleAff);
    /* Les tracés à moins de EPS du point et les textes dont le centre est à
       moins de VOISIN : l'index les rend tous (et d'autres, que les mêmes tests
       écartent). Leur ordre ne compte pas : les angles sont triés. */
    index.tracesPres(p, EPS, (a, b) => {
        const da = norme(moins(a, p));
        const db = norme(moins(b, p));
        if (da < EPS) {
            if (db > EPS)
                angles.push(angleAff(moins(b, p)));
            return;
        }
        if (db < EPS) {
            angles.push(angleAff(moins(a, p)));
            return;
        }
        const ab = moins(b, a);
        const t = ((p.x - a.x) * ab.x + (p.y - a.y) * ab.y) / (ab.x * ab.x + ab.y * ab.y);
        if (t > 0 && t < 1 && norme(moins(plus(a, fois(ab, t)), p)) < EPS) {
            angles.push(angleAff(ab), angleAff(fois(ab, -1)));
        }
    });
    index.boitesPres(p, VOISIN, (b) => {
        const d = moins({ x: b.x + b.largeur / 2, y: b.y + b.hauteur / 2 }, p);
        const n = norme(d);
        if (n > EPS && n < VOISIN)
            angles.push(angleAff(d));
    });
    if (angles.length === 0)
        return eloigne ?? { x: 0, y: -1 };
    angles.sort((a, b) => a - b);
    const secteurs = angles.map((a, i) => {
        const suivant = i + 1 < angles.length ? angles[i + 1] : angles[0] + 2 * Math.PI;
        return { milieu: (a + suivant) / 2, ouverture: suivant - a };
    });
    const plusLarge = Math.max(...secteurs.map((s) => s.ouverture));
    let meilleur = { x: 0, y: -1 };
    let score = -Infinity;
    for (const s of secteurs) {
        if (s.ouverture < plusLarge - 0.02)
            continue;
        const d = direction(s.milieu);
        const sc = (eloigne ? d.x * eloigne.x + d.y * eloigne.y : 0) - 0.001 * d.y;
        if (sc > score) {
            score = sc;
            meilleur = d;
        }
    }
    return meilleur;
}
/** La boîte d'un texte dont le centre est `c` (celle que rend `texteCentreV2`). */
export function boiteCentreeV2(c, largeur, hauteur) {
    return { x: c.x - largeur / 2, y: c.y - hauteur / 2, largeur, hauteur };
}
/** Combien de tracés et de boîtes une boîte de texte heurte — un effleurement ne compte pas. */
export function collisionsV2(b, obstacles, boites) {
    const r = { x: b.x + 0.5, y: b.y + 0.5, largeur: b.largeur - 1, hauteur: b.hauteur - 1 };
    let n = 0;
    for (const [a, z] of obstacles)
        if (segmentTraverse(a, z, r))
            n += 1;
    for (const q of boites)
        if (chevauchent(r, q))
            n += 1;
    return n;
}
/**
 * Le centre du nom d'un point. La direction du plus grand secteur libre
 * (`directionNom`) passe d'abord ; si la boîte y heurte encore quelque chose —
 * constat sur MAT-1-GEOM-PER : le nom « B » tombait sur la ligne de rappel
 * d'une cote, qui ne passe pas PAR le point —, seize autres directions sont
 * essayées, et la moins encombrée l'emporte, la plus proche de la préférée à
 * égalité.
 */
function placerNom(p, nom, index, directions, eloigne) {
    const { largeur, hauteur } = dimensionsTexteV2(nom);
    const prefere = directionNom(p, index, directions, eloigne);
    let meilleur = centreDecaleV2(p, prefere, RAYON_MARQUE_V2 + ECART_NOM, largeur, hauteur);
    let cout = Infinity;
    for (const d of [prefere, ...DIRECTIONS_NOM]) {
        const ecart = 1 - (d.x * prefere.x + d.y * prefere.y);
        const k = (n) => 10 * n + ecart;
        const seuil = cout - 1e-9;
        /* Arrêt anticipé : même sans rien heurter, cette direction ne gagnerait pas. */
        if (!(k(0) < seuil))
            continue;
        const c = centreDecaleV2(p, d, RAYON_MARQUE_V2 + ECART_NOM, largeur, hauteur);
        const n = index.compter(boiteCentreeV2(c, largeur, hauteur), plafondCollisions(k, seuil));
        if (k(n) < seuil) {
            cout = k(n);
            meilleur = c;
        }
    }
    return meilleur;
}
/** Les seize directions de repli d'un nom, tous les 22,5°. */
const DIRECTIONS_NOM = Array.from({ length: 16 }, (_, k) => direction((k * Math.PI) / 8));
/**
 * Le nombre de collisions à partir duquel une position perd : le plus petit
 * `n` tel que `cout(n)` ne passe plus sous `seuil` (`cout` croît avec `n`).
 * Au-delà, compter ne changerait rien au choix : l'index s'arrête.
 */
function plafondCollisions(cout, seuil) {
    if (seuil === Infinity)
        return Infinity;
    let n = 0;
    while (cout(n) < seuil)
        n += 1;
    return n;
}
/** La clé d'une étiquette dans un placement : l'`id` de son objet, ou son rang. */
const cleEtiquette = (o, rang) => o.id ?? `#${rang}`;
const placementUtilisable = (e, kx, ky, r) => e.echelle.kx === kx &&
    e.echelle.ky === ky &&
    e.echelle.xMin === r.xMin &&
    e.echelle.yMax === r.yMax;
/**
 * La marge du cadre `repere`, en unités d'affichage : de quoi loger la flèche
 * d'un axe, les graduations d'un axe posé au bord et le nom d'un point posé
 * sur le bord de la fenêtre.
 */
export const MARGE_CADRE_REPERE_V2 = 2.5 * TAILLE_POLICE_V2;
/** L'origine d'un objet, à recopier sur ses primitives — rien s'il n'en a pas. */
function origineDe(o) {
    if (o.id === undefined && o.ton === undefined)
        return null;
    return {
        ...(o.id === undefined ? {} : { source: o.id }),
        ...(o.ton === undefined ? {} : { ton: o.ton }),
    };
}
/** Une primitive, marquée de l'origine de son objet. Sans origine, la même primitive. */
function marquee(p, origine) {
    return origine === null ? p : { ...p, ...origine };
}
const cleArete = (p, q) => `${propre(p.x)},${propre(p.y)}|${propre(q.x)},${propre(q.y)}`;
/**
 * Le dessin CALCULÉ d'une figure du plan, en coordonnées d'affichage.
 *
 * La figure doit sortir de `normaliserFigureV2` : ce dessin ne revérifie rien,
 * il ne fait que calculer.
 */
export function dessinerPlan(f, options = {}) {
    return dessinerPlanAvecEtiquettes(f, options).liste;
}
/**
 * `dessinerPlan`, et le placement que son placeur a décidé : ce que l'atelier
 * garde du dernier dessin complet et repasse, pendant un glisser, dans
 * l'option `etiquettes`.
 */
export function dessinerPlanAvecEtiquettes(f, options = {}) {
    const r = f.repere;
    const cadreRepere = options.cadre === 'repere';
    const { kx, ky } = echellePlan(r, f.objets, cadreRepere);
    const aff = (p) => ({ x: (p.x - r.xMin) * kx, y: (r.yMax - p.y) * ky });
    const fige = options.etiquettes !== undefined && placementUtilisable(options.etiquettes, kx, ky, r)
        ? options.etiquettes
        : null;
    /* Les tracés ne servent qu'au placeur : quand tout est figé, on ne les
       relève pas (un cercle en donne 72). */
    const collecter = fige === null ||
        f.objets.some((o, rang) => (o.type === 'cote' && !fige.cotes.has(cleEtiquette(o, rang))) ||
            (o.type === 'point' && o.nom !== '' && !fige.noms.has(cleEtiquette(o, rang))));
    const etiquettes = etiquettesDe(f.objets);
    const grad = analyserAxes(r, etiquettes, kx === ky);
    const fond = [];
    /* Les SURFACES (polygones pleins, secteurs), dans l'ordre des objets : peintes
       sous tous les traits et tous les textes. Une surface « fond » évide celles
       qui la précèdent (tranche T28). */
    const surfaces = [];
    const axes = [];
    const traces = [];
    const cotes = [];
    const marques = [];
    const textes = [];
    const obstacles = [];
    const directionsAuPoint = new Map();
    const rangsArcs = new Map();
    const clePoint = (p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
    const t = TAILLE_POLICE_V2;
    /* Les libellés de graduation (leur rang dans `textes`), par axe, et les CONIQUES
       de la figure, en segments : un libellé que l'une d'elles traverse — celui du
       point où elle croise l'axe — est écarté d'elle après le tracé des objets. */
    const graduations = [];
    const coniquesTracees = [];
    /* ---- grille et axes */
    const x0 = borne(0, r.xMin, r.xMax);
    const y0 = borne(0, r.yMin, r.yMax);
    if (r.grille) {
        for (const v of grad.x.valeurs) {
            const bas = r.axes && grad.x.bandeEcrite ? y0 : r.yMin;
            fond.push(trait(aff({ x: v, y: bas }), aff({ x: v, y: r.yMax }), 'plein', 'grille'));
        }
        for (const v of grad.y.valeurs) {
            const gauche = r.axes && grad.y.bandeEcrite ? x0 : r.xMin;
            fond.push(trait(aff({ x: gauche, y: v }), aff({ x: r.xMax, y: v }), 'plein', 'grille'));
        }
    }
    if (r.axes) {
        /* Quand la figure écrit elle-même les libellés d'un axe, l'autre axe part
           de l'origine au lieu de traverser leur bande (le « 0 » d'un diagramme). */
        const debutX = aff({ x: grad.y.bandeEcrite ? x0 : r.xMin, y: y0 });
        const finX = aff({ x: r.xMax, y: y0 });
        const pointeX = { x: finX.x + PROLONGEMENT_AXE, y: finX.y };
        const debutY = aff({ x: x0, y: grad.x.bandeEcrite ? y0 : r.yMin });
        const finY = aff({ x: x0, y: r.yMax });
        const pointeY = { x: finY.x, y: finY.y - PROLONGEMENT_AXE };
        axes.push(trait(debutX, pointeX, 'plein', 'axe'), pointeFlecheV2(pointeX, { x: 1, y: 0 }, 'axe'));
        axes.push(trait(debutY, pointeY, 'plein', 'axe'), pointeFlecheV2(pointeY, { x: 0, y: -1 }, 'axe'));
        if (collecter)
            obstacles.push([debutX, pointeX], [debutY, pointeY]);
        const libelle = (v) => formaterNombre(v);
        /* Un axe gradué en π (CAP-SIN) écrit « π/2 », « 3π/2 » ; sinon, des nombres. */
        const libelleX = (v) => (r.enPi?.x === true ? texteEnPiV2(v) : libelle(v));
        const libelleY = (v) => (r.enPi?.y === true ? texteEnPiV2(v) : libelle(v));
        /* Axe des x : traits, puis libellés espacés d'au moins leur largeur. */
        const pasAffX = (grad.x.pas ?? 1) * kx;
        const largeurMax = Math.max(0, ...grad.x.valeurs.map((v) => dimensionsTexteV2(libelleX(v)).largeur));
        const tousLesX = Math.max(1, Math.ceil((largeurMax + 0.8 * t) / pasAffX));
        for (const v of grad.x.valeurs) {
            const p = aff({ x: v, y: y0 });
            axes.push(trait({ x: p.x, y: p.y - 3 }, { x: p.x, y: p.y + 3 }, 'plein', 'axe'));
            if (!grad.x.libellesCalcules || grad.x.pas === null)
                continue;
            if (Math.round(v / grad.x.pas) % tousLesX !== 0)
                continue;
            const txt = libelleX(v);
            const { largeur, hauteur } = dimensionsTexteV2(txt);
            /* Le libellé qui tombe sur l'axe des y (souvent « 0 ») passe à sa gauche
               quand l'axe des y descend plus bas : sinon il le chevauche. */
            const surAxeY = Math.abs(v - x0) < 1e-9 && r.yMin < y0;
            const cx = surAxeY ? p.x - 4 - largeur / 2 : p.x;
            graduations.push({ rang: textes.length, axe: 'x', v });
            textes.push(texteCentreV2({ x: cx, y: p.y + 5 + hauteur / 2 }, txt, 'graduation'));
        }
        const pasAffY = (grad.y.pas ?? 1) * ky;
        const tousLesY = Math.max(1, Math.ceil((1.5 * (MONTANTE + DESCENDANTE) * t) / pasAffY));
        const origineEcrite = grad.x.libellesCalcules &&
            x0 === 0 &&
            y0 === 0 &&
            grad.x.valeurs.some((v) => v === 0 && grad.x.pas !== null && Math.round(v / grad.x.pas) % tousLesX === 0);
        for (const v of grad.y.valeurs) {
            const p = aff({ x: x0, y: v });
            axes.push(trait({ x: p.x - 3, y: p.y }, { x: p.x + 3, y: p.y }, 'plein', 'axe'));
            if (!grad.y.libellesCalcules || grad.y.pas === null)
                continue;
            if (Math.round(v / grad.y.pas) % tousLesY !== 0)
                continue;
            const surAxeX = Math.abs(v - y0) < 1e-9;
            if (surAxeX && origineEcrite)
                continue;
            const txt = libelleY(v);
            const h = (MONTANTE + DESCENDANTE) * t;
            const cy = surAxeX && r.xMin < x0 ? p.y - 3 - h / 2 : p.y;
            if (!surAxeX)
                graduations.push({ rang: textes.length, axe: 'y', v });
            textes.push({
                type: 'texte',
                x: p.x - 5,
                y: cy + ((MONTANTE - DESCENDANTE) / 2) * t,
                texte: txt,
                ancre: 'fin',
                role: 'graduation',
                taille: t,
            });
        }
    }
    /* ---- l'extérieur de chaque côté de polygone, pour y poser les cotes.
       Par l'ORIENTATION du polygone, pas par son barycentre : sur une figure en
       L, le barycentre est dehors et enverrait une cote à l'intérieur. */
    const exterieurs = new Map();
    for (const o of f.objets) {
        if (o.type !== 'polygone')
            continue;
        const sens = aireSignee(o.sommets) > 0 ? 1 : -1;
        o.sommets.forEach((p, i) => {
            const q = o.sommets[(i + 1) % o.sommets.length];
            const u = unitaire(moins(q, p));
            if (u === null)
                return;
            const ext = sens > 0 ? { x: u.y, y: -u.x } : { x: -u.y, y: u.x };
            exterieurs.set(cleArete(p, q), ext);
            exterieurs.set(cleArete(q, p), ext);
        });
    }
    const tousPoints = f.objets
        .filter((o) => o.type === 'point')
        .map((o) => ({ x: o.x, y: o.y }));
    const centreFigure = barycentre(tousPoints) ?? {
        x: (r.xMin + r.xMax) / 2,
        y: (r.yMin + r.yMax) / 2,
    };
    /* Dès qu'un secteur est dans la figure, la surface d'un polygone se peint À PART. */
    const surfacesAPart = f.objets.some((o) => o.type === 'secteur');
    const arr = (x) => String(Math.round(x * 1000) / 1000);
    /** Les longueurs écrites le long d'un arc : posées après tous les tracés. */
    const textesDArc = [];
    /* ---- les objets */
    /* LE SENS DES HACHURES. Deux régions (demi-plans ou régions bornées par une
       courbe) hachurées dans le même sens se confondent : leur réunion a l'air
       d'une seule région, et leur intersection — la région-solution d'un
       système — ne se lit pas. La 2e est donc hachurée dans l'AUTRE sens (le
       miroir gauche-droite de la première), et l'intersection sort en quadrillé.
       Les suivantes alternent. Une figure à une seule région ne change pas. */
    let regionsHachurees = 0;
    const sensDeHachure = () => {
        const second = regionsHachurees % 2 === 1;
        regionsHachurees += 1;
        return second ? (p, largeur) => ({ x: largeur - p.x, y: p.y }) : (p) => p;
    };
    /* Une conique de l'énoncé gêne les graduations ; le tracé de l'élève, lui, bouge
       sous son doigt : il ne déplace aucun libellé. */
    const geneLesGraduations = (o) => r.axes && (o.ton === undefined || o.ton === 'enonce');
    /**
     * LA RÉGION D'UNE CONIQUE (tranche T38) : les hachures du demi-plan et de la
     * région d'une courbe — des parallèles à 45° de l'écran, coupées par la fenêtre —,
     * ici bornées par une courbe qui peut couper la même hachure plusieurs fois.
     * Chaque hachure est parcourue pas à pas, et chaque passage de la frontière est
     * cherché par dichotomie sur l'équation de la conique elle-même.
     *
     * Devant une frontière STRICTE, elles s'arrêtent à `RETRAIT_HACHURE_STRICTE_V2`
     * points d'elle, mesurés PERPENDICULAIREMENT (comme le demi-plan, T34) : la
     * distance d'un point à la conique est estimée par la valeur de l'équation
     * divisée par la norme de son gradient, exacte à la frontière.
     */
    const hachurerConique = (o, org) => {
        if (o.region === undefined)
            return;
        const signe = o.region === 'interieur' ? 1 : -1;
        const g = (X, Y) => signe * valeurConiqueV2(o, r.xMin + X / kx, r.yMax - Y / ky);
        const retrait = o.stricte === true ? RETRAIT_HACHURE_STRICTE_V2 : 0;
        /** Positif : le point d'affichage est dans la région (et, si elle est stricte, à l'écart de sa frontière). */
        const dedans = (X, Y) => {
            const v = g(X, Y);
            if (retrait === 0)
                return v;
            const pente = Math.hypot(g(X + 0.5, Y) - g(X - 0.5, Y), g(X, Y + 0.5) - g(X, Y - 0.5));
            return pente > 1e-12 ? v / pente - retrait : v;
        };
        const L = (r.xMax - r.xMin) * kx;
        const H = (r.yMax - r.yMin) * ky;
        const sens = sensDeHachure();
        const PAS = 2;
        for (let k = -H; k <= L + 1e-9; k += PAS_HACHURE_V2) {
            const X0 = Math.max(0, k);
            const X1 = Math.min(L, k + H);
            if (!(X1 > X0))
                continue;
            const n = Math.max(1, Math.ceil((X1 - X0) / PAS));
            const en = (i) => {
                const X = X0 + ((X1 - X0) * i) / n;
                return sens({ x: X, y: X - k }, L);
            };
            const passage = (dans, hors) => {
                let a = dans;
                let b = hors;
                for (let i = 0; i < 24; i += 1) {
                    const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
                    if (dedans(m.x, m.y) >= 0)
                        a = m;
                    else
                        b = m;
                }
                return a;
            };
            let debut = null;
            let avant = en(0);
            let avantDedans = dedans(avant.x, avant.y) >= 0;
            if (avantDedans)
                debut = avant;
            const fermer = (fin) => {
                if (debut !== null && Math.hypot(fin.x - debut.x, fin.y - debut.y) >= 1) {
                    traces.push(marquee(trait(debut, fin, 'plein', 'figure'), org));
                }
                debut = null;
            };
            for (let i = 1; i <= n; i += 1) {
                const p = en(i);
                const pDedans = dedans(p.x, p.y) >= 0;
                if (pDedans && !avantDedans)
                    debut = passage(p, avant);
                else if (!pDedans && avantDedans)
                    fermer(passage(avant, p));
                avant = p;
                avantDedans = pDedans;
            }
            if (avantDedans)
                fermer(avant);
        }
    };
    /** Le trait d'une conique : plein, ou en tirets si la figure le demande ou si sa région l'exclut. */
    const styleConique = (o) => o.stricte === true || (o.type !== 'cercle' && o.pointille === true) ? 'pointille' : 'plein';
    for (const o of f.objets) {
        const org = origineDe(o);
        switch (o.type) {
            case 'point':
                /* Une BORNE (tranche T42) a la taille d'une borne, pleine ou vide ; les
                   marques sont peintes après tous les traits : un point vide les coupe. */
                marques.push(marquee({
                    type: 'marque',
                    cx: aff(o).x,
                    cy: aff(o).y,
                    r: o.creux === undefined ? RAYON_MARQUE_V2 : RAYON_BORNE_V2,
                    ...(o.creux === true ? { creux: true } : {}),
                }, org));
                break;
            case 'etiquette':
                textes.push(marquee(texteCentreV2(aff(o), o.texte, 'etiquette'), org));
                break;
            case 'segment': {
                const a = aff(o.de);
                const b = aff(o.a);
                traces.push(marquee(trait(a, b, o.style, 'figure'), org));
                if (collecter)
                    obstacles.push([a, b]);
                break;
            }
            case 'droite': {
                const c = couperDroite(o.de, o.a, r);
                if (c === null)
                    break;
                const a = aff(c[0]);
                const b = aff(c[1]);
                traces.push(marquee(trait(a, b, o.style, 'figure'), org));
                if (collecter)
                    obstacles.push([a, b]);
                break;
            }
            case 'polygone': {
                const pts = o.sommets.map(aff);
                if ((surfacesAPart || o.contour === false) && o.plein !== false) {
                    /* Avec un secteur dans la figure, la surface du polygone se peint dans
                       l'ordre des surfaces — pour qu'un secteur « fond » puisse l'évider. */
                    surfaces.push(marquee({
                        type: 'surface',
                        d: `${pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${arr(p.x)} ${arr(p.y)}`).join(' ')} Z`,
                        points: pts,
                        remplissage: 'teinte',
                        calque: 'figure',
                    }, org));
                }
                /* Sans contour : la surface seule ; ses côtés ne gênent aucune étiquette. */
                if (o.contour === false)
                    break;
                traces.push(marquee({
                    type: 'polygone',
                    points: pts,
                    rempli: false,
                    calque: 'figure',
                    ...(surfacesAPart || o.plein === false ? { plein: false } : {}),
                }, org));
                if (collecter) {
                    pts.forEach((p, i) => obstacles.push([p, pts[(i + 1) % pts.length]]));
                }
                break;
            }
            case 'cercle': {
                const c = aff(o.centre);
                const rr = o.rayon * kx;
                traces.push(marquee({ type: 'cercle', cx: c.x, cy: c.y, r: rr, style: styleConique(o), calque: 'figure' }, org));
                const n = 72;
                const gene = geneLesGraduations(o);
                for (let i = 0; (collecter || gene) && i < n; i += 1) {
                    const a0 = (2 * Math.PI * i) / n;
                    const a1 = (2 * Math.PI * (i + 1)) / n;
                    const corde = [
                        plus(c, fois(direction(a0), rr)),
                        plus(c, fois(direction(a1), rr)),
                    ];
                    if (collecter)
                        obstacles.push(corde);
                    if (gene)
                        coniquesTracees.push(corde);
                }
                hachurerConique(o, org);
                break;
            }
            case 'ellipse':
            case 'hyperbole':
            case 'parabole': {
                const pointilles = [];
                if (o.type === 'hyperbole') {
                    const { x: h, y: k } = o.centre;
                    if (o.rectangle === true) {
                        const coins = [
                            { x: h - o.a, y: k - o.b },
                            { x: h + o.a, y: k - o.b },
                            { x: h + o.a, y: k + o.b },
                            { x: h - o.a, y: k + o.b },
                        ].map(aff);
                        coins.forEach((p, i) => pointilles.push([p, coins[(i + 1) % 4]]));
                    }
                    if (o.asymptotes === true) {
                        /* Les deux droites y − k = ±(b/a)(x − h), coupées par la fenêtre. */
                        for (const s of [-1, 1]) {
                            const c = couperDroite(o.centre, { x: h + o.a, y: k + s * o.b }, r);
                            if (c !== null)
                                pointilles.push([aff(c[0]), aff(c[1])]);
                        }
                    }
                }
                if (o.type === 'parabole' && o.directrice === true) {
                    /* La directrice : perpendiculaire à l'axe, à la distance c du sommet, à l'opposé du foyer. */
                    const horizontal = o.axe === 'horizontal';
                    const d = (horizontal ? o.sommet.x : o.sommet.y) - o.c;
                    const c = horizontal
                        ? couperDroite({ x: d, y: 0 }, { x: d, y: 1 }, r)
                        : couperDroite({ x: 0, y: d }, { x: 1, y: d }, r);
                    if (c !== null)
                        pointilles.push([aff(c[0]), aff(c[1])]);
                }
                const gene = geneLesGraduations(o);
                for (const [a, b] of pointilles) {
                    traces.push(marquee(trait(a, b, 'pointille', 'figure'), org));
                    if (collecter)
                        obstacles.push([a, b]);
                    /* Une directrice y = −2 passe SUR le libellé « −2 » : elle l'écarte aussi. */
                    if (gene)
                        coniquesTracees.push([a, b]);
                }
                const ellipse = o.type === 'ellipse'
                    ? { cx: aff(o.centre).x, cy: aff(o.centre).y, rx: o.a * kx, ry: o.b * ky }
                    : null;
                for (const troncon of tronconsConique(o, r)) {
                    const pts = troncon.map(aff);
                    traces.push(marquee({
                        type: 'polyligne',
                        points: pts,
                        style: styleConique(o),
                        calque: 'figure',
                        ...(ellipse === null ? {} : { ellipse }),
                    }, org));
                    for (let i = 1; i < pts.length; i += 1) {
                        const s = [pts[i - 1], pts[i]];
                        if (collecter)
                            obstacles.push(s);
                        if (gene)
                            coniquesTracees.push(s);
                    }
                }
                if (o.type === 'parabole' && o.foyer === true) {
                    const foyer = aff(foyersConiqueV2(o)[0]);
                    marques.push(marquee({ type: 'marque', cx: foyer.x, cy: foyer.y, r: RAYON_MARQUE_V2 }, org));
                }
                hachurerConique(o, org);
                break;
            }
            case 'arc':
            case 'secteur': {
                const c = aff(o.centre);
                const rr = o.rayon * kx;
                const tour = o.a - o.de >= 360 - 1e-9;
                /** Le point du cercle à l'angle `deg` (antihoraire ; l'écran a son y vers le bas). */
                const sur = (deg) => ({
                    x: c.x + rr * Math.cos((deg * Math.PI) / 180),
                    y: c.y - rr * Math.sin((deg * Math.PI) / 180),
                });
                const n = Math.max(8, Math.ceil((o.a - o.de) / 5));
                const points = Array.from({ length: n + 1 }, (_, i) => sur(o.de + ((o.a - o.de) * i) / n));
                const p0 = sur(o.de);
                const p1 = sur(o.a);
                const milieuArc = sur((o.de + o.a) / 2);
                /* Un vrai arc SVG. Antihoraire à l'écran : `sweep` vaut 0. Un tour entier
                   ne s'écrit pas d'un seul arc (ses deux bouts se confondent) : deux moitiés. */
                const versArc = tour
                    ? `A ${arr(rr)} ${arr(rr)} 0 1 0 ${arr(milieuArc.x)} ${arr(milieuArc.y)} A ${arr(rr)} ${arr(rr)} 0 1 0 ${arr(p0.x)} ${arr(p0.y)}`
                    : `A ${arr(rr)} ${arr(rr)} 0 ${o.a - o.de > 180 ? 1 : 0} 0 ${arr(p1.x)} ${arr(p1.y)}`;
                const cercle = { cx: c.x, cy: c.y, r: rr, de: o.de, a: o.a };
                const tracerLArc = (style) => {
                    traces.push(marquee({
                        type: 'arc',
                        d: `M ${arr(p0.x)} ${arr(p0.y)} ${versArc}`,
                        points,
                        calque: 'figure',
                        style,
                        cercle,
                    }, org));
                    for (let i = 1; collecter && i < points.length; i += 1)
                        obstacles.push([points[i - 1], points[i]]);
                };
                if (o.type === 'arc') {
                    tracerLArc(o.style);
                    if (o.texte !== null)
                        textesDArc.push({ o, c, rr, org });
                    break;
                }
                surfaces.push(marquee({
                    type: 'surface',
                    d: tour
                        ? `M ${arr(p0.x)} ${arr(p0.y)} ${versArc} Z`
                        : `M ${arr(c.x)} ${arr(c.y)} L ${arr(p0.x)} ${arr(p0.y)} ${versArc} Z`,
                    points: tour ? points : [c, ...points],
                    remplissage: o.remplissage,
                    calque: 'figure',
                    cercle,
                }, org));
                if (o.contour) {
                    tracerLArc('plein');
                    if (!tour) {
                        traces.push(marquee(trait(c, p0, 'plein', 'figure'), org));
                        traces.push(marquee(trait(c, p1, 'plein', 'figure'), org));
                        if (collecter)
                            obstacles.push([c, p0], [c, p1]);
                    }
                }
                break;
            }
            case 'fonction':
                /* L'asymptote verticale d'un logarithme, si la figure la demande : en
                   tirets, sur toute la hauteur de la fenêtre, SOUS la courbe. */
                /* L'asymptote HORIZONTALE y = k d'une fonction rationnelle, de même. */
                if (o.asymptote === true &&
                    o.fonction.famille === 'rationnelle' &&
                    o.fonction.k >= r.yMin &&
                    o.fonction.k <= r.yMax) {
                    const gauche = aff({ x: r.xMin, y: o.fonction.k });
                    const droite = aff({ x: r.xMax, y: o.fonction.k });
                    traces.push(marquee(trait(gauche, droite, 'pointille', 'figure'), org));
                    if (collecter)
                        obstacles.push([gauche, droite]);
                }
                if (o.asymptote === true &&
                    (o.fonction.famille === 'logarithmique' || o.fonction.famille === 'rationnelle') &&
                    o.fonction.h >= r.xMin &&
                    o.fonction.h <= r.xMax) {
                    const bas = aff({ x: o.fonction.h, y: r.yMin });
                    const haut = aff({ x: o.fonction.h, y: r.yMax });
                    traces.push(marquee(trait(bas, haut, 'pointille', 'figure'), org));
                    if (collecter)
                        obstacles.push([bas, haut]);
                }
                /* Les asymptotes d'une tangente : TOUTES celles de la fenêtre, en tirets, sous la courbe. */
                if (o.asymptote === true && o.fonction.famille === 'tangente') {
                    for (const xa of asymptotesTangenteV2(o.fonction, r.xMin, r.xMax)) {
                        const bas = aff({ x: xa, y: r.yMin });
                        const haut = aff({ x: xa, y: r.yMax });
                        traces.push(marquee(trait(bas, haut, 'pointille', 'figure'), org));
                        if (collecter)
                            obstacles.push([bas, haut]);
                    }
                }
                for (const troncon of tronconsFonction(o.fonction, o.domaine, r)) {
                    const pts = troncon.map(aff);
                    traces.push(marquee({
                        type: 'polyligne',
                        points: pts,
                        style: o.pointille === true ? 'pointille' : 'fort',
                        calque: 'courbe',
                    }, org));
                    for (let i = 1; collecter && i < pts.length; i += 1)
                        obstacles.push([pts[i - 1], pts[i]]);
                }
                break;
            case 'par_parties': {
                /* Chaque partie, comme une `fonction` bornée par son domaine ; ses bornes
                   pleines et vides, parmi les marques — peintes après tous les traits. */
                for (const partie of o.parties) {
                    for (const troncon of tronconsFonction(partie.fonction, partie.domaine, r)) {
                        const pts = troncon.map(aff);
                        traces.push(marquee({
                            type: 'polyligne',
                            points: pts,
                            style: o.pointille === true ? 'pointille' : 'fort',
                            calque: 'courbe',
                        }, org));
                        for (let i = 1; collecter && i < pts.length; i += 1)
                            obstacles.push([pts[i - 1], pts[i]]);
                    }
                }
                for (const borne of bornesDesPartiesV2(o.parties)) {
                    const q = borne.p;
                    if (q.x < r.xMin || q.x > r.xMax || q.y < r.yMin || q.y > r.yMax)
                        continue;
                    marques.push(marquee({
                        type: 'marque',
                        cx: aff(q).x,
                        cy: aff(q).y,
                        r: RAYON_BORNE_V2,
                        ...(borne.creux ? { creux: true } : {}),
                    }, org));
                }
                break;
            }
            case 'region': {
                /* La frontière : la courbe, en trait fort — ou en tirets si l'inégalité est stricte. */
                for (const troncon of tronconsFonction(o.fonction, null, r)) {
                    const pts = troncon.map(aff);
                    traces.push(marquee({
                        type: 'polyligne',
                        points: pts,
                        style: o.stricte ? 'pointille' : 'fort',
                        calque: 'courbe',
                    }, org));
                    for (let i = 1; collecter && i < pts.length; i += 1)
                        obstacles.push([pts[i - 1], pts[i]]);
                }
                /* Les hachures : celles du demi-plan — des parallèles à 45° de l'écran,
                   coupées par la fenêtre. Une courbe peut couper une hachure PLUSIEURS
                   fois : chaque hachure est parcourue pas à pas, et chaque passage de la
                   frontière est cherché par dichotomie sur la fonction elle-même. Comme
                   pour le demi-plan, ce remplissage n'est pas donné au placeur
                   d'étiquettes. */
                const dedans = (X, Y) => dansLaRegion(o.fonction, o.cote, r.xMin + X / kx, r.yMax - Y / ky);
                const L = (r.xMax - r.xMin) * kx;
                const H = (r.yMax - r.yMin) * ky;
                const sens = sensDeHachure();
                /* Le long d'une hachure, y croît toujours ; x croît, ou décroît dans le second sens. */
                const dx = sens({ x: 1, y: 0 }, 0).x;
                const PAS = 2;
                for (let k = -H; k <= L + 1e-9; k += PAS_HACHURE_V2) {
                    const X0 = Math.max(0, k);
                    const X1 = Math.min(L, k + H);
                    if (!(X1 > X0))
                        continue;
                    const n = Math.max(1, Math.ceil((X1 - X0) / PAS));
                    const en = (i) => {
                        const X = X0 + ((X1 - X0) * i) / n;
                        return sens({ x: X, y: X - k }, L);
                    };
                    /** Le passage de la frontière entre un point dedans et un point dehors. */
                    const passage = (dans, hors) => {
                        let a = dans;
                        let b = hors;
                        for (let t = 0; t < 24; t += 1) {
                            const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
                            if (dedans(m.x, m.y) >= 0)
                                a = m;
                            else
                                b = m;
                        }
                        return a;
                    };
                    /* Une frontière STRICTE doit se lire en tirets : une hachure qui la
                       touche bouche ses blancs. Elle s'arrête donc un peu avant — de ce
                       retrait, le long d'elle-même. */
                    const retrait = (o.stricte ? RETRAIT_HACHURE_STRICTE_V2 : 0) / Math.SQRT2;
                    let debut = null;
                    let avant = en(0);
                    let avantDedans = dedans(avant.x, avant.y) >= 0;
                    if (avantDedans)
                        debut = avant;
                    /** `aLaFrontiere` : ce bout est un passage de la courbe, pas le bord de la fenêtre. */
                    const fermer = (fin, aLaFrontiere) => {
                        const bout = aLaFrontiere ? { x: fin.x - dx * retrait, y: fin.y - retrait } : fin;
                        if (debut !== null && bout.y - debut.y >= 1 / Math.SQRT2) {
                            traces.push(marquee(trait(debut, bout, 'plein', 'figure'), org));
                        }
                        debut = null;
                    };
                    for (let i = 1; i <= n; i += 1) {
                        const p = en(i);
                        const pDedans = dedans(p.x, p.y) >= 0;
                        if (pDedans && !avantDedans) {
                            const c = passage(p, avant);
                            debut = { x: c.x + dx * retrait, y: c.y + retrait };
                        }
                        else if (!pDedans && avantDedans)
                            fermer(passage(avant, p), true);
                        avant = p;
                        avantDedans = pDedans;
                    }
                    if (avantDedans)
                        fermer(avant, false);
                }
                break;
            }
            case 'angle': {
                const s = aff(o.sommet);
                const va = moins(aff(o.de), s);
                const vb = moins(aff(o.a), s);
                const u = unitaire(va);
                const v = unitaire(vb);
                if (u === null || v === null)
                    break;
                const brasMin = Math.min(norme(va), norme(vb));
                let bissectrice;
                let rayonTexte;
                if (o.droit) {
                    const c = Math.min(10, 0.3 * brasMin);
                    traces.push(marquee({
                        type: 'carre_droit',
                        points: [
                            plus(s, fois(u, c)),
                            plus(s, plus(fois(u, c), fois(v, c))),
                            plus(s, fois(v, c)),
                        ],
                        style: 'plein',
                        calque: 'figure',
                    }, org));
                    bissectrice = unitaire(plus(u, v)) ?? normale(u);
                    rayonTexte = 1.5 * c;
                }
                else {
                    /* Plusieurs angles au même sommet : chacun son rayon, sinon deux arcs
                       adjacents se soudent en un seul (constat sur MAT-1-GEOM-ANG-V3-D03). */
                    const rang = rangsArcs.get(clePoint(s)) ?? 0;
                    rangsArcs.set(clePoint(s), rang + 1);
                    const rayon = Math.min(0.9 * brasMin, Math.min(20, Math.max(Math.min(8, 0.6 * brasMin), 0.28 * brasMin)) + 5 * rang);
                    const a0 = angleAff(u);
                    let delta = Math.atan2(u.x * v.y - u.y * v.x, u.x * v.x + u.y * v.y);
                    /* Angle plat : deux côtés possibles. On prend celui du haut de l'écran. */
                    if (Math.abs(u.x * v.y - u.y * v.x) < 1e-9 && u.x * v.x + u.y * v.y < 0) {
                        const haut = direction(a0 + Math.PI / 2).y < 0 ||
                            (Math.abs(direction(a0 + Math.PI / 2).y) < 1e-9 && direction(a0 + Math.PI / 2).x > 0);
                        delta = haut ? Math.PI : -Math.PI;
                    }
                    const n = Math.max(8, Math.ceil(Math.abs(delta) / (Math.PI / 24)));
                    const points = Array.from({ length: n + 1 }, (_, i) => plus(s, fois(direction(a0 + (delta * i) / n), rayon)));
                    const p0 = points[0];
                    const p1 = points[n];
                    const arr = (x) => String(Math.round(x * 1000) / 1000);
                    const d = `M ${arr(p0.x)} ${arr(p0.y)} A ${arr(rayon)} ${arr(rayon)} 0 0 ${delta > 0 ? 1 : 0} ${arr(p1.x)} ${arr(p1.y)}`;
                    traces.push(marquee({ type: 'arc', d, points, calque: 'figure' }, org));
                    bissectrice = direction(a0 + delta / 2);
                    rayonTexte = rayon;
                }
                const cle = clePoint(s);
                directionsAuPoint.set(cle, [...(directionsAuPoint.get(cle) ?? []), bissectrice, u, v]);
                if (o.texte !== null) {
                    const { largeur, hauteur } = dimensionsTexteV2(o.texte);
                    textes.push(marquee(texteCentreV2(centreDecaleV2(s, bissectrice, rayonTexte + 3, largeur, hauteur), o.texte, 'etiquette'), org));
                }
                break;
            }
            case 'vecteur': {
                const a = aff(o.de);
                const b = aff(o.a);
                const dx = b.x - a.x;
                const dy = b.y - a.y;
                const n = Math.hypot(dx, dy);
                if (!(n > 1e-9))
                    break;
                const d = { x: dx / n, y: dy / n };
                /* Le trait s'arrête au PIED de la pointe : poussé jusqu'au bout, il
                   dépasserait de la flèche et en émousserait la pointe. Un vecteur
                   plus court que sa pointe n'est plus qu'une pointe. */
                const pied = n > FLECHE_LONGUEUR
                    ? { x: b.x - d.x * FLECHE_LONGUEUR, y: b.y - d.y * FLECHE_LONGUEUR }
                    : a;
                traces.push(marquee(trait(a, pied, 'plein', 'figure'), org));
                traces.push(marquee(pointeFlecheV2(b, d, 'figure'), org));
                if (collecter)
                    obstacles.push([a, b]);
                break;
            }
            case 'demi_plan': {
                const style = o.stricte ? 'pointille' : 'plein';
                const c = couperDroite(o.de, o.a, r);
                if (c !== null) {
                    const a = aff(c[0]);
                    const b = aff(c[1]);
                    traces.push(marquee(trait(a, b, style, 'figure'), org));
                    if (collecter)
                        obstacles.push([a, b]);
                }
                /* La normale de la frontière, retournée vers le côté demandé. On la
                   garde en unités de FIGURE : « dessus » veut dire y croissant pour
                   l'élève, pas vers le bas de l'écran. */
                let nx = -(o.a.y - o.de.y);
                let ny = o.a.x - o.de.x;
                const vers = o.cote === 'dessus' ? ny : o.cote === 'dessous' ? -ny : o.cote === 'droite' ? nx : -nx;
                if (vers < 0) {
                    nx = -nx;
                    ny = -ny;
                }
                /* Une frontière STRICTE doit se lire en tirets : des hachures qui la touchent
                   bouchent ses blancs (constat sur `MAT-4-INEQ2`, `SP-04`). Elles s'arrêtent
                   donc à `RETRAIT_HACHURE_STRICTE_V2` points de la frontière — mesurés
                   PERPENDICULAIREMENT à elle, quel que soit l'angle où elles arrivent. La
                   fonction ci-dessous est affine à l'écran : son gradient donne ce que vaut
                   un point d'écart. Frontière large : aucun retrait, rien ne change. */
                const retrait = o.stricte ? RETRAIT_HACHURE_STRICTE_V2 * Math.hypot(nx / kx, ny / ky) : 0;
                /** Positif : le point d'affichage est dans le demi-plan (et, s'il est strict, à l'écart de sa frontière). */
                const dedans = (X, Y) => (r.xMin + X / kx - o.de.x) * nx + (r.yMax - Y / ky - o.de.y) * ny - retrait;
                const L = (r.xMax - r.xMin) * kx;
                const H = (r.yMax - r.yMin) * ky;
                const sens = sensDeHachure();
                /* Les hachures : des parallèles à 45° de l'écran, coupées par la
                   fenêtre puis par la frontière. Elles sont un REMPLISSAGE et non un
                   tracé — on ne les donne pas au placeur d'étiquettes, sinon aucun nom
                   ne trouverait plus où se poser dans un demi-plan hachuré. */
                for (let k = -H; k <= L + 1e-9; k += PAS_HACHURE_V2) {
                    const X0 = Math.max(0, k);
                    const X1 = Math.min(L, k + H);
                    if (!(X1 > X0))
                        continue;
                    let p0 = sens({ x: X0, y: X0 - k }, L);
                    let p1 = sens({ x: X1, y: X1 - k }, L);
                    const f0 = dedans(p0.x, p0.y);
                    const f1 = dedans(p1.x, p1.y);
                    if (f0 < 0 && f1 < 0)
                        continue;
                    if (f0 < 0 || f1 < 0) {
                        const t = f0 / (f0 - f1);
                        const coupe = { x: p0.x + (p1.x - p0.x) * t, y: p0.y + (p1.y - p0.y) * t };
                        if (f0 < 0)
                            p0 = coupe;
                        else
                            p1 = coupe;
                    }
                    if (Math.hypot(p1.x - p0.x, p1.y - p0.y) < 1)
                        continue;
                    traces.push(marquee(trait(p0, p1, 'plein', 'figure'), org));
                }
                break;
            }
            case 'cote':
                break;
        }
    }
    /* ---- LES GRADUATIONS QU'UNE CONIQUE TRAVERSE (tranche T38). Un cercle de rayon 5
       croise l'axe des y en (0 ; 5), À L'HORIZONTALE, juste où s'écrit « 5 » : le
       liseré du libellé coupait son trait. Le libellé glisse alors le long de son
       axe, du PLUS PETIT écart qui le dégage — d'un côté ou de l'autre de la courbe —
       et reste contre sa graduation, sans monter sur le libellé voisin. S'il n'y a
       pas de place à moins d'une hauteur de texte (une courbe qui croise l'axe en
       biais, entre deux libellés serrés), il passe de l'AUTRE CÔTÉ de son axe, face à
       sa graduation — et s'il y est traversé aussi, il ne bouge pas. Sans conique
       sur lui, aucun libellé ne bouge. */
    if (coniquesTracees.length > 0) {
        /* Ce que le liseré d'un chiffre efface autour de lui, et la demi-épaisseur du trait. */
        const GARDE = 2.5;
        /** Les CHIFFRES du libellé, sans la marge de la police sous la ligne de base ni au-dessus. */
        const chiffres = (g) => {
            const boite = boiteTexteV2(g);
            return {
                x: boite.x,
                y: g.y - 0.72 * g.taille,
                largeur: boite.largeur,
                hauteur: 0.72 * g.taille,
            };
        };
        const traverse = (b) => {
            const large = {
                x: b.x - GARDE,
                y: b.y - GARDE,
                largeur: b.largeur + 2 * GARDE,
                hauteur: b.hauteur + 2 * GARDE,
            };
            return coniquesTracees.some(([p, q]) => segmentTraverse(p, q, large));
        };
        for (const { rang, axe, v } of graduations) {
            const texte = textes[rang];
            if (!traverse(chiffres(texte)))
                continue;
            const voisins = graduations
                .filter((g) => g.rang !== rang)
                .map((g) => chiffres(textes[g.rang]));
            /* À écart égal, d'abord vers l'extérieur (loin de l'origine). */
            const dehors = axe === 'x' ? (v > 0 ? 1 : -1) : v > 0 ? -1 : 1;
            const plafond = dimensionsTexteV2(texte.texte).hauteur;
            chercher: for (let ecart = 1; ecart <= plafond; ecart += 1) {
                /* Le « 0 » de l'origine, écrit à gauche de l'axe des y, ne glisse que vers la gauche. */
                for (const sens of axe === 'x' && v === 0 ? [-1] : [dehors, -dehors]) {
                    const candidat = {
                        ...texte,
                        x: texte.x + (axe === 'x' ? sens * ecart : 0),
                        y: texte.y + (axe === 'y' ? sens * ecart : 0),
                    };
                    if (traverse(chiffres(candidat)))
                        continue;
                    /* Entre deux libellés, il reste au moins un point de blanc. */
                    const c = chiffres(candidat);
                    const boite = { x: c.x - 1, y: c.y - 1, largeur: c.largeur + 2, hauteur: c.hauteur + 2 };
                    if (voisins.some((autre) => chevauchent(boite, autre)))
                        continue;
                    textes[rang] = candidat;
                    break chercher;
                }
            }
            if (textes[rang] !== texte)
                continue;
            /* L'autre côté de l'axe : à droite de l'axe des y, au-dessus de l'axe des x. */
            const enFace = axe === 'y'
                ? { ...texte, x: texte.x + 10, ancre: 'debut' }
                : { ...texte, y: texte.y - 10 - plafond };
            if (!traverse(chiffres(enFace)))
                textes[rang] = enFace;
        }
    }
    /* ---- les cotes, APRÈS tous les tracés. Sur un côté de polygone, la cote
       va à l'extérieur. Sur un autre segment, elle essaie les deux côtés ; le
       texte essaie cinq positions le long de la ligne, à côté ou dans la ligne,
       et prend celle qui heurte le moins de choses — constat sur
       MAT-1-GEOM-PER : la hauteur HE d'un triangle, cotée « au-dessus » par
       défaut, faisait traverser son texte par le côté CE. */
    const fenetre = {
        x: 0,
        y: 0,
        largeur: (r.xMax - r.xMin) * kx,
        hauteur: (r.yMax - r.yMin) * ky,
    };
    /* L'index des collisions : construit à la première étiquette à placer, à
       partir de ce qui est déjà posé, puis tenu à jour. */
    let index = null;
    const indexDu = () => {
        if (index !== null)
            return index;
        const ix = new IndexCollisions(fenetre);
        for (const [a, b] of obstacles)
            ix.ajouterTrace(a, b);
        for (const texte of textes)
            ix.ajouterBoite(boiteTexteV2(texte));
        index = ix;
        return ix;
    };
    const ajouterObstacle = (s) => {
        if (!collecter)
            return;
        obstacles.push(s);
        index?.ajouterTrace(s[0], s[1]);
    };
    const ajouterTexte = (texte) => {
        textes.push(texte);
        index?.ajouterBoite(boiteTexteV2(texte));
    };
    const nomsPlaces = new Map();
    const cotesPlacees = new Map();
    /* ---- la longueur d'un arc : à l'EXTÉRIEUR du cercle, au milieu de l'arc, au
       plus près de lui. Si la place est prise, elle glisse le long de l'arc — au
       tiers, aux deux tiers — sans jamais passer à l'intérieur. */
    for (const { o, c, rr, org } of textesDArc) {
        const texte = o.texte;
        const { largeur, hauteur } = dimensionsTexteV2(texte);
        const poser = (part) => {
            const deg = o.de + (o.a - o.de) * part;
            const dehors = { x: Math.cos((deg * Math.PI) / 180), y: -Math.sin((deg * Math.PI) / 180) };
            return centreDecaleV2(plus(c, fois(dehors, rr)), dehors, ECART_TEXTE_ARC, largeur, hauteur);
        };
        const ix = indexDu();
        let centre = poser(0.5);
        let meilleur = Infinity;
        for (const part of [0.5, 0.35, 0.65, 0.25, 0.75]) {
            const k = (heurts) => 10 * heurts + Math.abs(part - 0.5);
            if (!(k(0) < meilleur - 1e-9))
                continue;
            const candidat = poser(part);
            const heurts = ix.compter(boiteCentreeV2(candidat, largeur, hauteur), plafondCollisions(k, meilleur - 1e-9));
            if (k(heurts) < meilleur - 1e-9) {
                meilleur = k(heurts);
                centre = candidat;
            }
        }
        ajouterTexte(marquee(texteCentreV2(centre, texte, 'cote_arc'), org));
    }
    for (let rang = 0; rang < f.objets.length; rang += 1) {
        const o = f.objets[rang];
        if (o.type !== 'cote')
            continue;
        const cle = cleEtiquette(o, rang);
        const org = origineDe(o);
        const p1 = aff(o.de);
        const p2 = aff(o.a);
        const { largeur, hauteur } = dimensionsTexteV2(o.texte);
        const versAff = (n) => unitaire({ x: n.x * kx, y: -n.y * ky }) ?? { x: 0, y: -1 };
        const longueur = norme(moins(p2, p1));
        /** Une pose candidate : côté `nd`, texte à `t` le long de la ligne, à côté ou DANS la ligne. */
        const poser = (nd, t, enLigne) => {
            const c1 = plus(p1, fois(nd, ECART_COTE));
            const c2 = plus(p2, fois(nd, ECART_COTE));
            const ancre = plus(c1, fois(moins(c2, c1), t));
            const centre = enLigne ? ancre : centreDecaleV2(ancre, nd, 3, largeur, hauteur);
            return { nd, c1, c2, t, enLigne, centre };
        };
        /** La normale du segment, en unités de figure : le côté d'une cote s'y rapporte. */
        const n = unitaire(normale(moins(o.a, o.de))) ?? { x: 0, y: 1 };
        const u0 = unitaire(moins(p2, p1)) ?? { x: 1, y: 0 };
        const trou = (Math.abs(u0.x) * largeur) / 2 + (Math.abs(u0.y) * hauteur) / 2 + 2;
        /** Dans la ligne, le texte doit laisser de la ligne des deux côtés. */
        const serre = (t) => t * longueur < trou + 6 || (1 - t) * longueur < trou + 6;
        const figee = fige?.cotes.get(cle);
        let choix;
        let sens;
        if (figee !== undefined) {
            /* Figée (un glisser) : même côté du segment, même endroit de la ligne.
               Des bouts inchangés reprennent le côté tel quel ; sinon, il suit le
               segment. */
            const memes = figee.de.x === o.de.x &&
                figee.de.y === o.de.y &&
                figee.a.x === o.a.x &&
                figee.a.y === o.a.y;
            choix = poser(memes ? figee.nd : versAff(fois(n, figee.sens)), figee.t, figee.enLigne && !serre(figee.t));
            sens = figee.sens;
        }
        else {
            /* Sur un côté de polygone, un seul côté permis : l'extérieur. Ailleurs,
               on préfère s'écarter du centre de la figure ; à égalité, le haut,
               puis la droite — et l'autre côté n'est pris que s'il est moins
               encombré. */
            const ext = exterieurs.get(cleArete(o.de, o.a));
            let cotesFigure;
            if (ext !== undefined)
                cotesFigure = [ext];
            else {
                const m = milieu(o.de, o.a);
                const sc = n.x * (m.x - centreFigure.x) + n.y * (m.y - centreFigure.y);
                const egalite = Math.abs(sc) < 1e-9 * (r.xMax - r.xMin);
                const versHaut = n.y > 1e-9 || (Math.abs(n.y) <= 1e-9 && n.x > 0);
                const prefere = (egalite ? versHaut : sc > 0) ? n : fois(n, -1);
                cotesFigure = [prefere, fois(prefere, -1)];
            }
            const cotes2 = cotesFigure.map(versAff);
            const ix = indexDu();
            let rangChoisi = 0;
            choix = poser(cotes2[0], 0.5, false);
            let meilleur = Infinity;
            cotes2.forEach((nd, rangCote) => {
                for (const t of [0.5, 0.35, 0.65, 0.2, 0.8]) {
                    for (const enLigne of [false, true]) {
                        if (enLigne && serre(t))
                            continue;
                        const k = (heurts) => 10 * heurts + (enLigne ? 0.5 : 0) + 0.3 * rangCote + Math.abs(t - 0.5);
                        const seuil = meilleur - 1e-9;
                        /* Arrêt anticipé : même sans rien heurter, cette pose ne gagnerait pas. */
                        if (!(k(0) < seuil))
                            continue;
                        const c = poser(nd, t, enLigne);
                        const heurts = ix.compter(boiteCentreeV2(c.centre, largeur, hauteur), plafondCollisions(k, seuil));
                        if (k(heurts) < seuil) {
                            meilleur = k(heurts);
                            choix = c;
                            rangChoisi = rangCote;
                        }
                    }
                }
            });
            const cote = cotesFigure[rangChoisi];
            sens = cote.x * n.x + cote.y * n.y < 0 ? -1 : 1;
        }
        cotesPlacees.set(cle, {
            de: o.de,
            a: o.a,
            nd: choix.nd,
            sens,
            t: choix.t,
            enLigne: choix.enLigne,
        });
        const { nd, c1, c2 } = choix;
        const rappel1 = [plus(p1, fois(nd, 3)), plus(p1, fois(nd, ECART_COTE + 4))];
        const rappel2 = [plus(p2, fois(nd, 3)), plus(p2, fois(nd, ECART_COTE + 4))];
        cotes.push(marquee(trait(rappel1[0], rappel1[1], 'plein', 'cote'), org));
        cotes.push(marquee(trait(rappel2[0], rappel2[1], 'plein', 'cote'), org));
        if (choix.enLigne) {
            /* Le texte est DANS la ligne de cote : elle s'interrompt pour lui —
               constat sur MAT-1-GEOM-PER : la hauteur d'un triangle aplati n'a de
               place, d'aucun côté, pour une ligne ET un texte posé à côté. */
            const ancre = plus(c1, fois(moins(c2, c1), choix.t));
            const g1 = moins(ancre, fois(u0, trou));
            const g2 = plus(ancre, fois(u0, trou));
            cotes.push(marquee(trait(c1, g1, 'plein', 'cote'), org), marquee(trait(g2, c2, 'plein', 'cote'), org));
            ajouterObstacle([c1, g1]);
            ajouterObstacle([g2, c2]);
            ajouterObstacle(rappel1);
            ajouterObstacle(rappel2);
        }
        else {
            cotes.push(marquee(trait(c1, c2, 'plein', 'cote'), org));
            ajouterObstacle([c1, c2]);
            ajouterObstacle(rappel1);
            ajouterObstacle(rappel2);
        }
        const ud = unitaire(moins(c2, c1));
        if (ud !== null && norme(moins(c2, c1)) >= 4 * FLECHE_LONGUEUR) {
            cotes.push(marquee(pointeFlecheV2(c2, ud, 'cote'), org), marquee(pointeFlecheV2(c1, fois(ud, -1), 'cote'), org));
        }
        ajouterTexte(marquee(texteCentreV2(choix.centre, o.texte, 'cote'), org));
    }
    /* ---- les noms, en dernier : ils esquivent tout ce qui est déjà posé —
       tracés, lignes de cote, textes, et les disques des autres points. */
    const pointsAff = f.objets.filter((o) => o.type === 'point').map((o) => aff(o));
    const centreAff = barycentre(pointsAff);
    let disquesRanges = false;
    const indexNoms = () => {
        const ix = indexDu();
        if (!disquesRanges) {
            for (const q of pointsAff) {
                ix.ajouterBoite(boiteCentreeV2(q, 2 * RAYON_MARQUE_V2, 2 * RAYON_MARQUE_V2));
            }
            disquesRanges = true;
        }
        return ix;
    };
    /* Le nom fléché d'un vecteur (tranche T43), AVANT les noms des points :
       près du milieu de la flèche, du côté libre (`nom-de-vecteur.ts`). */
    for (const o of f.objets) {
        if (o.type !== 'vecteur' || o.nom === undefined)
            continue;
        const org = origineDe(o);
        const { largeur, hauteur } = dimensionsTexteV2(o.nom);
        const air = AIR_FLECHE_NOM * t;
        const ix = indexNoms();
        const c = placerNomDeVecteurV2(aff(o.de), aff(o.a), largeur, hauteur + air, 0.45 * Math.min(kx, ky), (boite) => ix.compter(boite));
        const texte = texteCentreV2({ x: c.x, y: c.y + air / 2 }, o.nom, 'nom');
        const fleche = flecheDuNomV2(o.nom, c, largeur, texte.y, t);
        traces.push(marquee(trait(fleche.trait[0], fleche.trait[1], 'plein', 'figure'), org));
        traces.push(marquee({ type: 'polygone', points: [...fleche.pointe], rempli: true, calque: 'figure' }, org));
        ix.ajouterBoite(boiteCentreeV2(c, largeur, hauteur + air));
        ajouterTexte(marquee(texte, org));
    }
    for (let rang = 0; rang < f.objets.length; rang += 1) {
        const o = f.objets[rang];
        /* Un point sans nom (point de l'élève, atelier graphique) n'écrit rien ;
           la normalisation n'en laisse passer aucun. */
        if (o.type !== 'point' || o.nom === '')
            continue;
        const p = aff(o);
        const cle = cleEtiquette(o, rang);
        const place = fige?.noms.get(cle);
        const c = place !== undefined
            ? centreNomFige(place, p)
            : placerNom(p, o.nom, indexNoms(), directionsAuPoint.get(clePoint(p)) ?? [], centreAff ? unitaire(moins(p, centreAff)) : null);
        nomsPlaces.set(cle, { point: p, centre: c });
        ajouterTexte(marquee(texteCentreV2(c, o.nom, 'nom'), origineDe(o)));
    }
    /* Les points VIDES d'abord, puis les pleins (tranche T42) : un point plein au
       même endroit qu'un vide reste visible — c'est la règle d'une fonction (le
       plein l'emporte), et, dans la superposition du tuteur, le point attendu
       n'est jamais caché par le fond opaque d'un point vide de l'élève. */
    const vides = marques.filter((p) => p.type === 'marque' && p.creux === true);
    const pleines = marques.filter((p) => !(p.type === 'marque' && p.creux === true));
    const primitives = [
        ...fond,
        ...axes,
        ...surfaces,
        ...traces,
        ...cotes,
        ...vides,
        ...pleines,
        ...textes,
    ];
    const placement = {
        echelle: { kx, ky, xMin: r.xMin, yMax: r.yMax },
        noms: nomsPlaces,
        cotes: cotesPlacees,
    };
    if (cadreRepere) {
        const m = MARGE_CADRE_REPERE_V2;
        const viewBox = {
            x: -m,
            y: -m,
            largeur: fenetre.largeur + 2 * m,
            hauteur: fenetre.hauteur + 2 * m,
        };
        return {
            liste: {
                espace: 'plan',
                taille: f.taille,
                viewBox,
                rapport: viewBox.largeur / viewBox.hauteur,
                primitives,
            },
            etiquettes: placement,
        };
    }
    return { liste: fermerListe('plan', f.taille, primitives, fenetre), etiquettes: placement };
}
/**
 * Le cadre final : tout ce qui est dessiné, UNION `cadreMin` (la fenêtre du
 * repère pour le plan), plus une marge. Le rapport est rendu avec.
 */
function fermerListe(espace, taille, primitives, cadreMin) {
    const e = englobantV2(primitives) ?? cadreMin;
    const x0 = Math.min(e.x, cadreMin.x) - MARGE_CADRE;
    const y0 = Math.min(e.y, cadreMin.y) - MARGE_CADRE;
    const x1 = Math.max(e.x + e.largeur, cadreMin.x + cadreMin.largeur) + MARGE_CADRE;
    const y1 = Math.max(e.y + e.hauteur, cadreMin.y + cadreMin.hauteur) + MARGE_CADRE;
    const viewBox = { x: x0, y: y0, largeur: x1 - x0, hauteur: y1 - y0 };
    return { espace, taille, viewBox, rapport: viewBox.largeur / viewBox.hauteur, primitives };
}
//# sourceMappingURL=figure-v2.js.map